import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import './shell.css';
import { BannerContext } from './banner';
import { railItems, navTree, pageGroups, pageTitles, pageGroupId, navLabel, rawNavLabel, setNavCaptionOverrides, setAiAuditFormNav, setBacklogNav, setDocumentCategoryNav, documentCategoryOf, collectGroupLeafIds, canonMod, NAV_CHORDS, navPaletteTargets, type Badge, type NavGroupChild, type NavItem } from './navData';
import { useHotkeys, setHotkeyOverrides, setNavPaletteItems, type HotkeyDef } from '../components/hotkeys';
import { focusFirstFormControl } from '../hooks/useAutoFocusFirstControl';
import { resolveNavIcon } from './legacyNavIcons';   // global legacy-icon resolver (2026-09-18)
import { AI_AUDIT_FORMS, aiFormNavId } from '../features/audit/aiAuditForms';
import { NAV_VIEW_MAPPING, NAV_DASHBOARD_MAPPING, registerDashboardNav } from './navPermissions';
import navRegistry from '../../public/nav-registry.json';
import { canSeeNavItem, normCaption, buildNavActionMap } from './navPermissions';
import { useAuthStore } from '../auth/store';
import { touchTab, clearTabs, sidFromToken, usernameFromToken, staleWindowFor, shouldReleaseOnPageHide } from '../auth/tabRegistry';
import { watchIdle, resetActivity } from '../auth/idleTimeout';
import { warn as warnDlg } from '../components/confirm';
import { api } from '../api/client';
import { setClientTimeZone, setActiveFormDateMode } from '../utils/datetime';
import { stepDateInputValue } from '../utils/dateStep';
import { setLoaderSymbol, setLoaderColor, setProgressStyle, setProgressColor } from '../components/loading';
import { setSdmsRollbackUi } from '../utils/sdmsRollbackUi';
import { applyAppTheme, PENDING_THEME_SAVE_KEY } from '../theme/useAppTheme';
import { ThemeSwitcher } from '../components/ThemeSwitcher';
import { NavIcon } from '../components/NavIcon';
import { setAuditTrailConfig, type AuditFormConfig } from '../utils/auditConfig';
import { fuzzyMatch } from '../utils/fuzzyMatch';
import { navMatchesTerm, navChildHasSearchHit, navGroupHasSearchHit } from './navSearch';
import { HomePage } from '../features/home/HomePage';
import { ModuleListView } from '../features/modules/ModuleListView';
import { IcpMassPickPage } from '../features/modules/IcpMassPickPage';
import { MessageLibraryPage } from '../features/system/messageLibrary/MessageLibraryPage';
import { AuditTrailPage } from '../features/audit/AuditTrailPage';
import { TrendAnalysisPage } from '../features/trend/TrendAnalysisPage';
import { MdlStudiesPage } from '../features/qaqc/mdl/MdlStudiesPage';
import { AvaInsightPage } from '../features/ava/AvaInsightPage';
import { ExecutiveDashboardPage } from '../features/executive/ExecutiveDashboardPage';
import PerformanceAnalyticsPage from '../features/performance/PerformanceAnalyticsPage';
import ResultEntryExpressPage from '../features/analysis/ResultEntryExpressPage';
import DailyQcPage from '../features/analysis/dailyqc/DailyQcPage';
import ResultEntryFormulaPage from '../features/analysis/ResultEntryFormulaPage';
import SdmsFormulaPage from '../features/analysis/SdmsFormulaPage';
import NotificationCenterPage from '../features/notifications/NotificationCenterPage';
import { AlertBell } from '../features/notifications/inbox/AlertBell';
import { AlertPopupHost } from '../features/notifications/inbox/AlertPopupHost';
import { startInboxPolling } from '../features/notifications/inbox/inboxStore';
import { setAlertPopupSettings, type AlertPopupSettings } from '../features/notifications/inbox/alertPopupSettings';
import SampleBacklogPage from '../features/backlog/SampleBacklogPage';
import BacklogXmlGridPage from '../features/backlog/BacklogXmlGridPage';
import JobProgressPage  from '../features/backlog/JobProgressPage';
import TestProgressPage from '../features/backlog/TestProgressPage';
import SamplingAllocationPage from '../features/sampling/SamplingAllocationPage';
import SamplingHistoryPage from '../features/sampling/SamplingHistoryPage';
import SiteSchedulingFrequencyPage from '../features/sampling/SiteSchedulingFrequencyPage';
import SdmsSequenceSetupPage from '../features/analysis/SdmsSequenceSetupPage';
import { ScientificDataTablePage } from '../features/settings/ScientificDataTablePage';
import { ContractLabTestPricingPage } from '../features/settings/ContractLabTestPricingPage';
import { ParameterDefaultPage } from '../features/settings/ParameterDefaultPage';
import { SdmsTemplateBuilderListPage } from '../features/settings/SdmsTemplateBuilderListPage';
import { TemplateFormulaPage } from '../features/settings/TemplateFormulaPage';
import PlmReferenceLibraryPage from '../features/analysis/plm/PlmReferenceLibraryPage';
import PlmContaminationPage from '../features/analysis/plm/PlmContaminationPage';
import RequestRegistrationPage from '../features/clientRequests/RequestRegistrationPage';
import DataPackageTestViewPage from '../features/settings/DataPackageTestViewPage';
import FieldTestsPage from '../features/settings/FieldTestsPage';
import ServiceAllocationPage from '../features/clientRequests/ServiceAllocationPage';
import ServiceSchedulingPage from '../features/clientRequests/ServiceSchedulingPage';
import ServiceTrackingPage from '../features/clientRequests/ServiceTrackingPage';
import RouteTrackingPage from '../features/clientRequests/RouteTrackingPage';
import ReagentPrepLogPage from '../features/reagentprep/ReagentPrepLogPage';
import LltSearchPage from '../features/reagentprep/LltSearchPage';
import LltDepletionPage from '../features/reagentprep/LltDepletionPage';
import OpenQuotesPage from '../features/accounting/quotes/OpenQuotesPage';
import QuotesTrackingPage from '../features/accounting/quotes/QuotesTrackingPage';
import ScheduledTaskPage from '../features/sampling/ScheduledTaskPage';
import SampleRegistrationPage from '../features/sampling/SampleRegistrationPage';
import PreLoginSettingsPage from '../features/settings/PreLoginSettingsPage';
import SampleReceiptNotificationPage from '../features/sampling/SampleReceiptNotificationPage';
import SampleContainerPhCheckPage from '../features/sampling/SampleContainerPhCheckPage';
import SampleInPage from '../features/sampling/SampleInPage';
import SampleOutPage from '../features/sampling/SampleOutPage';
import SampleDisposalPage from '../features/sampling/SampleDisposalPage';
import SampleInOutLogPage from '../features/sampling/SampleInOutLogPage';
import SampleLocationPage from '../features/sampling/SampleLocationPage';
import SampleDisposalHistoryPage from '../features/sampling/SampleDisposalHistoryPage';
import FieldDataTrackingPage from '../features/sampling/FieldDataTrackingPage';
import FieldDataEntryPage from '../features/sampling/FieldDataEntryPage';
import FieldDataValidationPage from '../features/sampling/FieldDataValidationPage';
import FieldDataApprovalPage from '../features/sampling/FieldDataApprovalPage';
import SampleTransferPage from '../features/sampling/SampleTransferPage';
import FieldIntakePage from '../features/sampling/fieldIntake/FieldIntakePage';
import SampleCancellationPage from '../features/sampling/sampleCancellation/SampleCancellationPage';
import RegulatoryCompliancePage from '../features/settings/RegulatoryCompliancePage';
import RoundOffTestPage from '../features/settings/RoundOffTestPage';
import DwrcCategoriesPage from '../features/settings/DwrcCategoriesPage';
import DwrcSubCategoriesPage from '../features/settings/DwrcSubCategoriesPage';
import UserActivityPage from '../features/settings/UserActivityPage';
import ExceptionLogPage from '../features/settings/ExceptionLogPage';
import DefaultSettingsPage from '../features/settings/DefaultSettingsPage';
import FormVisibilityPage from '../features/settings/FormVisibilityPage';
import AuditTrailConfigurationPage from '../features/settings/AuditTrailConfigurationPage';
import DashboardDesignerPage from '../features/backlog/DashboardDesignerPage';
import DashboardPreviewPage from '../features/backlog/DashboardPreviewPage';
import DashboardsPage from '../features/dashboards/DashboardsPage';
import { DashboardViewerPage } from '../features/dashboards/DashboardViewerPage';
import { backlogDashboardId, isXmlGridBacklog } from '../features/dashboards/backlogDashboard';
import MorAdhocReportingPage from '../features/reporting/MorAdhocReportingPage';
import MorReportingPage from '../features/reporting/MorReportingPage';
import MorTemplateSetupPage from '../features/settings/MorTemplateSetupPage';
import SuboutListPage from '../features/subout/SuboutListPage';
import SuboutRegistrationPage from '../features/subout/suboutRegistration/SuboutRegistrationPage';
import SuboutTestOrderPage from '../features/subout/suboutRegistration/SuboutTestOrderPage';
import AnalysisListPage from '../features/analysis/AnalysisListPage';
import SdmsResultEntryPage from '../features/analysis/SdmsResultEntryPage';
import PlmPage from '../features/analysis/plm/PlmPage';
import PlmDailyQcListPage from '../features/analysis/plm/PlmDailyQcListPage';
import ReportingListPage from '../features/reporting/ReportingListPage';
import EddSpreadsheetPage from '../features/reporting/EddSpreadsheetPage';
import CustomReportBuilderPage from '../features/reporting/CustomReportBuilderPage';
import ReportBuilderPage from '../features/reporting/ReportBuilderPage';
import { BatchReportingPage } from '../features/reporting/BatchReportingPage';
import ExternalDataImportPage from '../features/reporting/ExternalDataImportPage';
import LimitsPage from '../features/settings/LimitsPage';
import ServerMonitorPage from '../features/servermonitor/ServerMonitorPage';
import SqlQueryConsolePage from '../features/queryConsole/SqlQueryConsolePage';
import DatabaseErdPage from '../features/toolbox/databaseErd/DatabaseErdPage';
import SdmsTemplateBuilderPage from '../features/toolbox/sdmsTemplateBuilder/SdmsTemplateBuilderPage';
import ServerHistoryPage from '../features/servermonitor/ServerHistoryPage';
import EmailAlertsPage from '../features/settings/EmailAlertsPage';
import AppConfigPage from '../features/settings/AppConfigPage';
import ClientVersionControlPage from '../features/settings/ClientVersionControlPage';
import AlpacaAppConfigPage from '../features/settings/AlpacaAppConfigPage';
import ScreenAutoLockPage from '../features/settings/ScreenAutoLockPage';
import ReportIdFormatPage from '../features/settings/ReportIdFormatPage';
import DateFilterPage from '../features/settings/DateFilterPage';
import FlutterDefaultSettingsPage from '../features/settings/FlutterDefaultSettingsPage';
import MetrcIncomingPage from '../features/settings/MetrcIncomingPage';
import KeyboardShortcutsPage from '../features/settings/KeyboardShortcutsPage';
import ActConfigPage from '../features/settings/act/ActConfigPage';
import WorkflowConfigListPage from '../features/workflow/WorkflowConfigListPage';
import TrainingRecordsPage from '../features/qaqc/training/TrainingRecordsPage';
import ControlChartPage from '../features/qaqc/controlChart/ControlChartPage';
import { MyDetailsPage } from '../features/security/MyDetailsPage';
import UsersAdminPage from '../features/security/UsersAdminPage';
import RolesAdminPage from '../features/security/RolesAdminPage';
import MacAdminPage from '../features/security/MacAdminPage';
import NavigationManagerPage from '../features/security/NavigationManagerPage';
import { PaymentTermsPage } from '../features/accounting/PaymentTermsPage';
import { RegionPage } from '../features/accounting/RegionPage';
import VersionLogPage from '../features/maintenance/VersionLogPage';
// System Management + Accounting full module (merged 2026-06-26)
import { PaymentTermsSetupPage } from '../features/accounting/PaymentTermsSetupPage';
import { AnalysisDepartmentChainPage } from '../features/settings/analysisDepartmentChain/AnalysisDepartmentChainPage';
import { CityPage } from '../features/settings/systemManagement/CityPage';
import { QualifierListPage } from '../features/documentManagement/QualifierListPage';
import { DocumentsPage } from '../features/documentManagement/DocumentsPage';
import { documentsApi } from '../features/documentManagement/documentsApi';
import { CompanyPage } from '../features/settings/systemManagement/CompanyPage';
import { DivisionDepartmentPage } from '../features/settings/systemManagement/DivisionDepartmentPage';
import { GlossaryPage } from '../features/settings/systemManagement/GlossaryPage';
import { MessageEditorPage } from '../features/settings/systemManagement/MessageEditorPage';
import { CountryPage } from '../features/settings/systemManagement/CountryPage';
import { StatePage } from '../features/settings/systemManagement/StatePage';
import { DepartmentPage } from '../features/settings/systemManagement/DepartmentPage';
import { PositionPage } from '../features/settings/systemManagement/PositionPage';
import { TimeZonesPage } from '../features/settings/systemManagement/TimeZonesPage';
import { CurrentLanguagePage } from '../features/settings/systemManagement/CurrentLanguagePage';
// ICM Operations (2026-09-21)
import RequisitionListPage from '../features/icm/operations/RequisitionListPage';
import RequisitionDetailPage from '../features/icm/operations/RequisitionDetailPage';
import RequisitionReviewListPage from '../features/icm/operations/RequisitionReviewListPage';
import PurchaseOrderListPage from '../features/icm/operations/PurchaseOrderListPage';
import PurchaseOrderDetailPage from '../features/icm/operations/PurchaseOrderDetailPage';
import ReceiveItemsPage from '../features/icm/operations/ReceiveItemsPage';
import DistributionListPage from '../features/icm/operations/DistributionListPage';
import ConsumptionListPage from '../features/icm/operations/ConsumptionListPage';
import FractionalConsumptionPage from '../features/icm/operations/FractionalConsumptionPage';
import DisposalListPage from '../features/icm/operations/DisposalListPage';
import VendorCoaListPage from '../features/icm/operations/VendorCoaListPage';
import ItemInitListPage from '../features/icm/operations/ItemInitListPage';
import StockWatchPage from '../features/icm/alerts/StockWatchPage';
import StockAlertPage from '../features/icm/alerts/StockAlertPage';
import ExpirationAlertPage from '../features/icm/alerts/ExpirationAlertPage';
import ItemRequisitionTrackingPage from '../features/icm/tracking/ItemRequisitionTrackingPage';
import ItemProcessTrackingPage from '../features/icm/tracking/ItemProcessTrackingPage';
import CancelledRequisitionPage from '../features/icm/tracking/CancelledRequisitionPage';
import ItemTransactionHistoryPage from '../features/icm/tracking/ItemTransactionHistoryPage';
import LtCoaTrackingPage from '../features/icm/tracking/LtCoaTrackingPage';
import { confirmDiscardIfUnsaved, installUnsavedTracking, resetUnsavedTracking } from '../hooks/useUnsavedGuard';
import { clearPendingRecord } from './navigateTo';
import { GridLayoutProvider, type GridLayoutDefaults } from '../components/AppDataGrid';

/** Data Center grids = the G5 (XAF) ListView look: every data column a fixed
 *  140px (user request 2026-10-13). The matching 22px rows / ~30px header are
 *  in shell.css under .content[data-nav-group="grp-datacenter"]. */
const DATA_CENTER_GRID_LAYOUT: GridLayoutDefaults = {
  fixedColumnWidth: 140,
  // Email values are long — 140px cut them to "user033@example…." (user
  // request 2026-10-13, Client Contacts). Applies to every DC `email` column.
  columnWidths: { email: 240 },
  // Data Center lists open at 100 rows per page (user request 2026-10-13).
  pageSize: 100,
  // Lists open ordered by the first column, ascending (owner request
  // 2026-10-13); ModuleListReader applies it server-side for dc-*/dc2-*/histres.
  defaultSortFirstColumn: true,
};
const NO_GRID_LAYOUT: GridLayoutDefaults = {};

const badgeText: Record<Badge, string> = { intel: 'Intel', view: 'view', new: 'new', ai: 'AI' };

/* Nav glyph rendering (TI_EMOJI + NavIcon) MOVED to the shared
   components/NavIcon.tsx (2026-07-03) so the sidebar AND every form's
   <FormTitle> render the SAME glyph — the on-page title icon can no longer
   drift from its navigation entry. */

function initials(name?: string): string {
  if (!name) return 'AI';
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] ?? '') + (p[1]?.[0] ?? '')).toUpperCase() || 'AI';
}

/**
 * v5 ALPACA Intelligence shell — rail + sidebar (brand, collapsible
 * nav-groups, BTSoft footer) + slim topbar + content. Module pages are
 * placeholders; each form is built one-by-one in later iterations.
 */
export function AppShell() {
  const navigate = useNavigate();
  const { moduleKey, id: detailId } = useParams();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const permissions = useAuthStore((s) => s.permissions);
  // The LOGIN username, for the service-account nav gate.  Priority:
  //   1. the JWT's `name` claim — the SAME value the API's IsServiceUser()
  //      reads, so the sidebar and the server can never disagree, and a
  //      session minted before AuthUserDto.UserName existed still resolves;
  //   2. the login payload's userName;
  //   3. user.name — the Employee DISPLAY name, kept only as a last resort.
  // (3) alone was the bug: it equals "Service" on one database and
  // "Service User" on another, so Form Config vanished on the latter.
  const authToken = useAuthStore((s) => s.token);
  const loginUserName = usernameFromToken(authToken) || user?.name || '';
  const setPermissions = useAuthStore((s) => s.setPermissions);

  // Bumped to re-fetch the nav-config feed + permissions immediately after a
  // Navigation Manager OR an Assign-Dashboard save (both dispatch the
  // 'ai-nav-config-changed' window event) — so the sidebar updates WITHOUT a page
  // reload. Declared HERE (above the permissions + dynamic-backlog-nav effects) so
  // BOTH depend on it and refresh together: a just-assigned Sample Backlog dashboard
  // needs the catalog (membership) AND allowedDashboards (visibility) both refreshed
  // to appear. (2026-10-06: added the Assign-Dashboard auto-refresh.)
  const [navRefreshTick, setNavRefreshTick] = useState(0);
  useEffect(() => {
    const h = () => setNavRefreshTick(t => t + 1);
    window.addEventListener('ai-nav-config-changed', h);
    return () => window.removeEventListener('ai-nav-config-changed', h);
  }, []);

  // ── Self-heal stale nav permissions ──────────────────────────────────────
  // Whenever the shell mounts (or the signed-in user changes) re-fetch the
  // user's permission set so a stale localStorage entry can't outlive
  // a server-side role change.  Falls open silently on failure.
  useEffect(() => {
    if (!user) return;
    // ALWAYS re-fetch perms on mount (background) so a full page reload reflects
    // permission changes made SINCE login — e.g. a Sample Backlog dashboard newly
    // assigned via Settings → Assign Dashboard To User appears on refresh WITHOUT
    // a re-login, matching ALPACA (whose server-rendered nav reflects the latest
    // grant on the next page load). The cached perms (login inline payload, or a
    // token-only restore) render FIRST so there's no nav flash; this fetch then
    // updates them in place. `permissions` is deliberately NOT in the effect deps
    // (only [user, setPermissions]) so the setPermissions below can't loop it.
    // (Was `if (permissions != null) return;`, which cached perms until re-login —
    // so a just-granted dashboard never showed on refresh. Fixed 2026-08-31.)
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/v1/auth/me/permissions');
        const data = (res.data as {
          data: {
            isAdmin: boolean;
            roles: string;
            allowedNavViews:   Array<{ navigationView: string | null; navigationCaption: string | null }>;
            allowedDashboards: Array<{ dashboardId: string }>;
            formVisibility?: Record<string, boolean>;
            dataReviewNav?: {
              sdmsResultValidation: boolean;
              sdmsResultApproval: boolean;
              resultValidation: boolean;
              resultApproval: boolean;
              fieldDataValidation: boolean;
              fieldDataApproval: boolean;
              multiTab?: boolean;
            };
            avaEnabled?: boolean;
            helpCenterEnabled?: boolean;
            navActions?: Array<{
              navigationView: string | null;
              canRead: boolean; canWrite: boolean; canCreate: boolean; canDelete: boolean;
            }>;
          };
        }).data;
        if (cancelled) return;
        setPermissions({
          isAdmin: data.isAdmin,
          roles:   data.roles,
          // Lowercase to match canSeeNavItem, which compares
          // `allowedViews.has(v.toLowerCase())` (NAV_VIEW_MAPPING values are
          // lowercased at the comparison). WITHOUT this, non-admin users had every
          // view-mapped form hidden — the Set held mixed-case NavigationView names
          // that never matched the lowercased lookup (admins bypass, so only the
          // service/admin account looked correct). Mirrors the store login path
          // (auth/store.ts) and allowedDashboards just below. (2026-08-11 fix)
          allowedViews: data.allowedNavViews
            .map(v => (v.navigationView ?? '').toLowerCase())
            .filter(Boolean),
          allowedDashboards: (data.allowedDashboards ?? [])
            .map(d => (d.dashboardId ?? '').toLowerCase())
            .filter(Boolean),
          // Captions are the GLOBAL fallback — when our NavigationView mapping
          // misses a variant (e.g. Samplecheckin_ListView_Copy_Registration vs
          // Samplecheckin_ListView_SampleRegistration), the display label still
          // matches, so the item shows for the right users.
          allowedCaptions: data.allowedNavViews
            .map(v => v.navigationCaption ?? '')
            .filter(Boolean),
          // Global "Form Setup" gate — formId → visible. ANDed with the
          // per-user permission below in canSeeNavItem (a form hidden here is
          // hidden for everyone except PROTECTED_FROM_HIDE ids).
          formVisibility: data.formVisibility ?? {},
          // Data Review nav visibility (see hiddenForms below) — dropping this
          // field here would silently re-show all 4 items on any session that
          // takes this self-heal path instead of a fresh login.
          dataReviewNav: data.dataReviewNav,
          // AVA Insight visibility — mirrors ALPACA App Config "AVA Enabled".
          avaEnabled: data.avaEnabled,
          // Help Center header-link visibility — MUST survive this self-heal path
          // too, else the link flashes on a hard refresh (stored perms show it) then
          // vanishes when this refresh overwrites the perms without it.
          helpCenterEnabled: data.helpCenterEnabled,
          // Per-form action permissions (New/Edit/Delete gating) from the user's
          // MAC levels — must survive the self-heal path too, else buttons the
          // MAC hides would re-appear on a token-only session restore.
          navActions: buildNavActionMap(data.navActions),
        });
      } catch {
        // 401 / network error — keep whatever's in the store
      }
    })();
    return () => { cancelled = true; };
    // navRefreshTick: re-fetch permissions when an Assign-Dashboard / Navigation
    // Manager save fires 'ai-nav-config-changed', so a just-granted dashboard's Oid
    // lands in allowedDashboards and the new nav item passes canSeeNavItem — no reload.
  }, [user, setPermissions, navRefreshTick]);

  // Whether a 2nd-signature approval stage is required (Default Settings →
  // Report Signing).  Drives the "Report Approval" sidebar item: it only shows
  // when approval is on (when off, validation advances straight to delivery).
  const [reportApprovalRequired, setReportApprovalRequired] = useState(false);

  // Field Data Validation / Approval sidebar visibility — driven by
  // Settings → Default Settings (Sampling Management, NavigationItemNameID
  // 'FieldDataReview1' / 'FieldDataReview2').  When a stage's Select flag is
  // off, its nav item hides (matches GoLive RecreateNavigationItems()). Read
  // from `permissions.dataReviewNav` (inlined into the login payload) — see the
  // hiddenForms comment; NOT a post-mount fetch, which flashed the item visible.

  // Data Review workflow config (Settings → Default Settings, items 5-9) — in
  // ALPACA these DefaultSetting.Select flags are BOTH the getstatus() workflow
  // gate (AnalysisWriter.ApplySdmsSampleStatusProgressionAsync) AND what turns
  // the corresponding sidebar nav item on/off. Read from `permissions.dataReviewNav`
  // (see hiddenForms below) — inlined into the login/permissions payload so it's
  // synchronously available on first render, not fetched here separately.

  // ── Client timezone (ADR-0008 MIGRATION-TEMPORARY) ───────────────────────
  // Fetch the configured client timezone once after sign-in and hand it to the
  // shared fmtLocal* helpers so still-Central (mode-b) wire values render in the
  // viewer's local time.  Falls back to the helper default on any failure.
  // Deleted with the rest of the client-zone path once every table is UTC.
  // Concurrent-login: whether seat validation is on + how often to heartbeat
  // (from GET /api/v1/app-config, set in the effect below).
  const [heartbeatCfg, setHeartbeatCfg] = useState<{ enabled: boolean; seconds: number }>(
    { enabled: false, seconds: 60 });
  // Screen Auto Lock (ALPACA's dbo.ScreenAutoLock): minutes of USER inactivity
  // before sign-out. Null = ALPACA has no row, so the behaviour stays off.
  const [autoLockMinutes, setAutoLockMinutes] = useState<number | null>(null);
  // Lets the idle watcher call the CURRENT sign-out without re-subscribing every
  // render (handleLogout is re-created each time and declared further down).
  const handleLogoutRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      // Retry once after a short delay on failure — several of the values this
      // fetch drives fail OPEN on an unset/undefined state for UX reasons, so a
      // transient failure here isn't just "keep the old default", it silently
      // freezes those settings at the wrong value for the rest of the session
      // with no trace. A single retry covers the common transient case (brief
      // network blip right after login); a console.warn on final failure makes
      // a genuine outage diagnosable instead of invisible.
      for (let attempt = 1; attempt <= 2; attempt++) {
        try {
          // silentLoading: a background poll shouldn't flash the global loader.
          const res = await api.get('/api/v1/app-config', { silentLoading: true } as never);
          const d = (res.data as { data?: { clientTimeZone?: string; formDateModes?: Record<string, string>; loaderSymbol?: string; sdmsRollbackUi?: string; loaderColor?: string; progressBarStyle?: string; progressBarColor?: string; reportApprovalRequired?: boolean; auditTrailConfig?: Record<string, AuditFormConfig>; userTheme?: string; concurrentLoginEnabled?: boolean; sessionHeartbeatSeconds?: number; screenAutoLockMinutes?: number; hotkeys?: Record<string, string>; alertPopup?: Partial<AlertPopupSettings> } })?.data;
          if (cancelled) return;
          setHeartbeatCfg({ enabled: d?.concurrentLoginEnabled === true, seconds: d?.sessionHeartbeatSeconds ?? 60 });
          setAutoLockMinutes(typeof d?.screenAutoLockMinutes === 'number' ? d.screenAutoLockMinutes : null);
          // Per-user theme (follows the user across devices), applied AFTER login.
          // A login-page pick (pre-auth, stashed in localStorage) wins and is saved
          // now; otherwise apply the server-saved theme (null → keep the local one).
          let pendingTheme: string | null = null;
          try { pendingTheme = localStorage.getItem(PENDING_THEME_SAVE_KEY); } catch { /* ignore */ }
          if (pendingTheme) {
            applyAppTheme(pendingTheme);
            void api.put('/api/v1/app-config/user-theme', { theme: pendingTheme },
              { silentLoading: true } as never).catch(() => { /* non-fatal */ });
            try { localStorage.removeItem(PENDING_THEME_SAVE_KEY); } catch { /* ignore */ }
          } else if (d?.userTheme) {
            applyAppTheme(d.userTheme);
          }
          if (d?.clientTimeZone) setClientTimeZone(d.clientTimeZone);
          if (d?.formDateModes) setFormDateModes(d.formDateModes);
          setReportApprovalRequired(d?.reportApprovalRequired === true);
          // Field Data Validation / Approval nav visibility is no longer read here
          // — it's inlined into the login/permissions payload (permissions.dataReviewNav)
          // so the sidebar has the right answer on first render (user report 2026-07-23).
          // Global audit-trail config (standing rule 11) — drives the per-form
          // audit button + reason-on-edit/delete app-wide.
          setAuditTrailConfig(d?.auditTrailConfig);
          // Apply the client-configured loader symbol/colour + progress-bar style/colour (§18.6).
          // Loader/progress COLOUR follows the active theme accent by DEFAULT (the CSS token
          // `--ai-loader-color` / `--ai-progress-color` fall back to `var(--brand-accent)`).
          // Only pin a literal colour when the client explicitly chose a NON-default override,
          // so changing the theme recolours the loaders too (2026-06-27).
          const BRAND_DEFAULT = '#e8670a';
          if (d?.loaderSymbol) setLoaderSymbol(d.loaderSymbol);
          setSdmsRollbackUi(d?.sdmsRollbackUi);   // 'classic' | 'enhanced' — SDMS rollback modal (§18.6)
          // Notification Center → Pop-up Settings: where / how long the in-app alert pop-up shows.
          setAlertPopupSettings(d?.alertPopup);
          if (d?.loaderColor && d.loaderColor.toLowerCase() !== BRAND_DEFAULT) setLoaderColor(d.loaderColor);
          if (d?.progressBarStyle) setProgressStyle(d.progressBarStyle);
          if (d?.progressBarColor && d.progressBarColor.toLowerCase() !== BRAND_DEFAULT) setProgressColor(d.progressBarColor);
          // Per-tenant keyboard-shortcut overrides (ADR-0013 Phase 3 part 2) — re-key
          // the whole registry so remapped actions take effect app-wide. Empty map = defaults.
          setHotkeyOverrides(d?.hotkeys ?? {});
          return;   // success — done, skip the retry
        } catch (err) {
          if (cancelled) return;
          if (attempt === 2) {
            // eslint-disable-next-line no-console
            console.warn('[AppShell] /api/v1/app-config failed after retry — client timezone, ' +
              'loader/progress config, audit-trail config, and Data Review nav visibility may ' +
              'be showing stale/default values for this session.', err);
            return;
          }
          await new Promise((r) => setTimeout(r, 500));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Concurrent-login heartbeat: keeps this session live so an active user is
  // never idle-timed-out (a 401 SESSION_EVICTED flows through the axios
  // interceptor, which signs out + shows the dialog).
  //
  // CLOSING THE BROWSER RELEASES THE LICENCE IMMEDIATELY (2026-07-25).
  //
  // Every tab of one sign-in shares ONE JWT, therefore one `sid`, therefore one
  // session row — so a naive `pagehide` beacon would revoke the session the
  // user's OTHER TABS are still using and sign them out mid-work. That is why it
  // was previously left to the idle sweep. But then a browser that was simply
  // closed held its licence for the full idle timeout, locking the next person
  // out for five minutes for no reason (reported 2026-07-25).
  //
  // `tabRegistry` resolves both: each tab claims a slot renewed by the heartbeat
  // below, and on `pagehide` a tab releases the SESSION only when no other live
  // tab of this browser remains. A crashed tab leaves a stale slot that ages out,
  // and if every tab dies at once the server-side idle sweep still reclaims the
  // session — this path only ever makes release faster, never slower.
  // In-app (bell) alerts: poll the signed-in user's inbox every ~30 s (quietly — no loader).
  // Stops, and clears the bell, on sign-out.
  useEffect(() => {
    if (!user) return;
    return startInboxPolling();
  }, [user]);

  useEffect(() => {
    if (!user || !heartbeatCfg.enabled) return;
    const sid = sidFromToken(useAuthStore.getState().token);
    const staleMs = staleWindowFor(heartbeatCfg.seconds);

    const beat = () => {
      if (sid) touchTab(sid, staleMs);      // renew this tab's slot alongside the beat
      void api.post('/api/v1/session/heartbeat', {}, { silentLoading: true } as never).catch(() => { /* interceptor handles evict */ });
    };
    const ms = Math.max(15, heartbeatCfg.seconds) * 1000;
    beat();                       // claim liveness at once, don't wait a full interval
    const timer = setInterval(beat, ms);

    // `pagehide` (not `beforeunload`) — it is the event that actually fires on
    // mobile and on bfcache navigations. `persisted` means the page is being
    // frozen into the back/forward cache and may be restored, so it is NOT a
    // close and must not release anything.
    const onPageHide = (e: PageTransitionEvent) => {
      const token = useAuthStore.getState().token;
      // One testable decision (tabRegistry.shouldReleaseOnPageHide): skips a
      // bfcache freeze, skips when other tabs of this browser are still open.
      if (!shouldReleaseOnPageHide(
        { persisted: e.persisted, sid, hasToken: !!token }, staleMs)) return;
      // fetch(keepalive) rather than sendBeacon: the endpoint is [Authorize] and
      // sendBeacon cannot set an Authorization header. keepalive lets the request
      // outlive the page, which is the whole point here.
      try {
        void fetch(`${api.defaults.baseURL ?? ''}/api/v1/session/end`, {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: '{}',
        }).catch(() => { /* best-effort; idle sweep is the backstop */ });
      } catch { /* ditto */ }
    };
    window.addEventListener('pagehide', onPageHide);

    return () => {
      clearInterval(timer);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [user, heartbeatCfg.enabled, heartbeatCfg.seconds]);

  // SCREEN AUTO LOCK — sign out after N minutes of USER inactivity, using
  // ALPACA's own dbo.ScreenAutoLock value so both products behave the same.
  //
  // ALPACA measures HTTP request inactivity, which cannot work here: the SPA
  // heartbeats every 60 s whatever the person is doing, so requests never stop
  // while a tab is open. Before this, Intelligence had NO idle sign-out at all —
  // only a "browser is gone" detector — so a tab left open overnight held its
  // concurrent-user licence until the 24-hour token expired. Tracking real user
  // activity is the faithful equivalent.
  //
  // Signing out through the SAME handler as the button is deliberate: it revokes
  // the session (releasing the licence at once) and writes the access-log ending,
  // rather than just dropping the token locally.
  useEffect(() => {
    if (!user || !autoLockMinutes || autoLockMinutes <= 0) return;
    return watchIdle(
      () => autoLockMinutes,
      () => {
        handleLogoutRef.current?.();
        // Told plainly, and on the blocking modal, so it is not mistaken for a
        // crash or a lost password — the user was signed out, on purpose.
        try {
          void warnDlg(
            `You were signed out after ${autoLockMinutes} minutes without activity. Please sign in again.`,
            { title: 'Signed out for inactivity' });
        } catch { /* provider not mounted — the redirect still happens */ }
      },
    );
  }, [user, autoLockMinutes]);

  // Runtime nav-caption overrides — reflect an admin's Navigation Manager rename in the
  // live sidebar without a code change (CLAUDE.md §0d).  Renames are stored in the shared
  // dbo.NavigationItem.NavigationCustomCaption column (the same override ALPACA displays);
  // the feed returns ONLY rows that HAVE a custom caption, so a label is overridden solely
  // when an admin set one — curated labels stay untouched otherwise.  Applies to both leaf
  // forms (AI via nav-registry, legacy via NAV_VIEW_MAPPING) and group/module headers
  // (auto-resolved from a child item's Parent → group NavigationId).
  const [, setCapTick] = useState(0);
  // Admin-set module sort: sidebar group id → sort number (drives the live group order).
  const [moduleSort, setModuleSort] = useState<Record<string, number>>({});
  // Admin-set form sort: sidebar nav id → sort number (re-orders items inside a group).
  const [formSort, setFormSort] = useState<Record<string, number>>({});
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/v1/navigation/captions', { silentLoading: true } as never);
        const pairs = ((res.data as { data?: { view?: string; navigationId?: string; parent?: string; customCaption?: string; dashboardId?: string; moduleName?: string; sort?: number; originalCaption?: string }[] })?.data) ?? [];
        if (cancelled) return;

        // Build lookup tables from the feed (custom captions only; all lower-cased keys).
        const byView = new Map<string, string>();       // NavigationView → custom caption (leaf)
        const byNavId = new Map<string, string>();      // NavigationId   → custom caption (group + leaf)
        const viewToParent = new Map<string, string>(); // NavigationView → Parent (a group's NavigationId)
        const byDashboard = new Map<string, string>();  // DashboardData.Oid → custom caption
        const byModule = new Map<string, { caption?: string; sort?: number }>(); // normalized module name → meta
        const viewSort = new Map<string, number>();     // NavigationView → AISortOrder (form order)
        // Fallback: originalCaption (DB NavigationCaption) → custom caption.  Used when the
        // NavigationView-based lookup misses — e.g. legacy forms whose DB view name differs
        // from the nav-registry's AI_ view, or forms not yet in NAV_VIEW_MAPPING.
        const byCaption = new Map<string, string>();    // DB NavigationCaption.lower() → custom caption
        for (const p of pairs) {
          if (p.moduleName) { byModule.set(canonMod(p.moduleName), { caption: (p.customCaption ?? '').trim() || undefined, sort: p.sort ?? undefined }); continue; }
          const cap = (p.customCaption ?? '').trim();
          if (p.view) {
            const v = p.view.toLowerCase();
            if (p.parent) viewToParent.set(v, p.parent.toLowerCase());
            if (p.sort != null) viewSort.set(v, p.sort);
            if (cap) {
              byView.set(v, cap);
              if (p.originalCaption) byCaption.set(p.originalCaption.toLowerCase().trim(), cap);
            }
          }
          if (p.dashboardId && cap) { byDashboard.set(p.dashboardId.toLowerCase(), cap); continue; }
          if (p.navigationId && cap) byNavId.set(p.navigationId.toLowerCase(), cap);
        }

        // nav id → its primary NavigationView: AI items from nav-registry, legacy items
        // from NAV_VIEW_MAPPING (first listed view is the canonical one).
        const idToView: Record<string, string> = {};
        for (const e of (navRegistry as { items?: { id: string; view: string }[] }).items ?? []) {
          if (e.view) idToView[e.id] = e.view;
        }
        for (const [id, views] of Object.entries(NAV_VIEW_MAPPING)) {
          if (!idToView[id] && views?.[0]) idToView[id] = views[0];
        }

        const overrides: Record<string, string> = {};
        // (a) Leaf forms — apply the admin's custom caption when one exists.
        // Primary match: by NavigationView (exact); fallback: by original DB caption text.
        for (const [id, view] of Object.entries(idToView)) {
          let custom = byView.get(view.toLowerCase());
          // Fallback: DB NavigationCaption matches the sidebar's curated label.
          // Handles forms where the DB row's NavigationView ≠ nav-registry's AI_ view.
          if (!custom) {
            const label = rawNavLabel(id);
            if (label) custom = byCaption.get(label.toLowerCase());
          }
          if (custom && custom !== (rawNavLabel(id) ?? '')) overrides[id] = custom;
        }
        // (a2) Items not covered by idToView (no nav-registry entry and no NAV_VIEW_MAPPING entry):
        // scan all navTree leaves and apply byCaption when the sidebar label matches a DB original.
        for (const e of navTree) {
          if (e.kind !== 'group') continue;
          for (const lid of collectGroupLeafIds(e.group)) {
            if (overrides[lid]) continue;  // already matched by view
            const label = rawNavLabel(lid);
            if (!label) continue;
            const custom = byCaption.get(label.toLowerCase());
            if (custom && custom !== label) overrides[lid] = custom;
          }
        }
        // (b) Group / module headers — resolve from a child item's Parent (a NavigationId)
        // → custom caption.  No per-group mapping; groups with no customized DB row keep
        // their curated label.
        for (const e of navTree) {
          if (e.kind !== 'group') continue;
          let groupCaption: string | undefined;
          for (const lid of collectGroupLeafIds(e.group)) {
            const view = idToView[lid];
            const parent = view ? viewToParent.get(view.toLowerCase()) : undefined;
            const cap = parent ? byNavId.get(parent) : undefined;
            if (cap) { groupCaption = cap; break; }
          }
          if (groupCaption && groupCaption !== (rawNavLabel(e.group.id) ?? e.group.label)) {
            overrides[e.group.id] = groupCaption;
          }
        }
        // (c) Dashboard / backlog nav items — NAV_DASHBOARD_MAPPING is navId → [dashboardOid];
        // apply the dashboard's custom caption to its nav id.
        if (byDashboard.size > 0) {
          for (const [id, oids] of Object.entries(NAV_DASHBOARD_MAPPING)) {
            for (const oid of oids) {
              const custom = byDashboard.get(oid.toLowerCase());
              if (custom && custom !== (rawNavLabel(id) ?? '')) { overrides[id] = custom; break; }
            }
          }
        }
        // (d) Modules — match a sidebar GROUP to a module by normalized label, then apply the
        // admin's custom caption (relabel the group header) + sort (reorder the groups).
        const grpSort: Record<string, number> = {};
        for (const e of navTree) {
          if (e.kind !== 'group') continue;
          const m = byModule.get(canonMod(e.group.label));
          if (!m) continue;
          if (m.caption && m.caption !== (rawNavLabel(e.group.id) ?? e.group.label)) overrides[e.group.id] = m.caption;
          if (m.sort != null) grpSort[e.group.id] = m.sort;
        }
        setModuleSort(grpSort);
        // (e) Form sort — map each sidebar nav id → its form's AISortOrder (via its view), so
        // the items inside a group can be re-ordered by the admin-set sort.
        const fSort: Record<string, number> = {};
        for (const [id, view] of Object.entries(idToView)) {
          const s = viewSort.get(view.toLowerCase());
          if (s != null) fSort[id] = s;
        }
        setFormSort(fSort);
        setNavCaptionOverrides(overrides);
        setCapTick(t => t + 1);   // re-render so navLabel/navMeta pick up the new captions
      } catch {
        // keep the curated labels on 401 / network error
      }
    })();
    return () => { cancelled = true; };
  }, [user, navRefreshTick]);

  // ── "Audit Trail Log AI" per-form nav (user request 2026-07-10) ──────────────
  // Fetch which AI-owned forms have ACTUALLY written audit rows (all-time) and
  // list each as its own leaf under the "Audit Trail Log AI" group, so the
  // sidebar itself shows exactly which forms are audited in AI.  Only forms with
  // data appear (per the user's choice); the consolidated "All AI Forms" leaf
  // stays first.  Wide sinceDate → "has ever been audited", not just recently.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/v1/audit/forms',
          { params: { sinceDate: '2000-01-01' }, silentLoading: true } as never);   // yyyy-MM-dd = all-time
        if (cancelled) return;
        const list = ((res.data as { data?: { formName: string; rows: number }[] })?.data) ?? [];
        const items = list
          .filter(f => f.rows > 0 && AI_AUDIT_FORMS.has(f.formName))       // AI-owned forms that have data
          .sort((a, b) => (b.rows - a.rows) || a.formName.localeCompare(b.formName))  // most-active first, then A→Z
          .map(f => ({ id: aiFormNavId(f.formName), label: f.formName, count: f.rows }));
        setAiAuditFormNav(items);
        setCapTick(t => t + 1);   // re-render so the sidebar picks up the new leaves
      } catch {
        // leave the group with just its consolidated "All AI Forms" leaf on error
      }
    })();
    return () => { cancelled = true; };
  }, [user, navRefreshTick]);

  // ── DYNAMIC "Sample Backlog" nav (2026-10-06) ────────────────────────────────
  // Build the To Do List → Sample Backlog subgroup from the dashboards ACTUALLY
  // assigned to that navigation item in dbo.AssignDashboardToUserDepartment
  // (/api/v1/backlog/nav-dashboards) instead of the hardcoded navData list — so a
  // dashboard assigned to "Sample Backlog" in Settings → Assign Dashboard To User
  // appears here automatically, exactly like legacy ALPACA. A KNOWN dashboard keeps
  // its existing nav id (so it still renders as the native grid — its id stays in
  // XML_GRID_BACKLOG); a NEW one gets a `bl-dyn-<oid>` id that routes to the
  // DevExpress <DashboardViewerPage>. Per-user VISIBILITY is unchanged — canSeeNavItem
  // gates each item on allowedDashboards, so registering id→Oid is enough for both
  // routing and the visibility check. On empty/failed fetch the static list is kept.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/v1/backlog/nav-dashboards', { silentLoading: true } as never);
        if (cancelled) return;
        const rows = ((res.data as { data?: { dashboardId?: string; title?: string }[] })?.data) ?? [];
        if (rows.length === 0) return;   // keep the curated static list if nothing came back
        // Reverse map Oid → existing static nav id, so a known dashboard reuses its
        // id (and thus its native grid) rather than getting a second viewer entry.
        const oidToKnownId = new Map<string, string>();
        for (const [navId, oids] of Object.entries(NAV_DASHBOARD_MAPPING)) {
          for (const o of oids) oidToKnownId.set(o.toLowerCase(), navId);
        }
        const items: { id: string; label: string }[] = [];
        for (const r of rows) {
          const oid = (r.dashboardId ?? '').toLowerCase();
          if (!oid) continue;
          const id = oidToKnownId.get(oid) ?? `bl-dyn-${oid}`;
          registerDashboardNav(id, oid);          // routing (backlogDashboardId) + visibility gate (canSeeNavItem)
          items.push({ id, label: r.title ?? '' });
        }
        setBacklogNav(items);                       // rebuild the subgroup's children
        setCapTick(t => t + 1);                     // re-render so the sidebar picks up the new leaves
      } catch {
        // keep the hardcoded Sample Backlog list on 401 / network error
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Documents ▸ one nav leaf per DOCUMENT CATEGORY (G5 parity, owner request
     2026-10-12). Same shape as the Sample Backlog feed above: fetch → rebuild the
     subgroup → bump the tick so the sidebar re-renders. The endpoint already
     returns only categories holding something READABLE, so a category whose last
     document retires drops out of the menu by itself — which is exactly what
     ALPACA's own generator does. On error the seeded plain "Documents" leaf
     stays, so the form is never unreachable. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Through the feature's OWN accessor, not a raw api.get: these endpoints
        // answer in the standard {success,data} envelope, and reading res.data
        // instead of res.data.data silently yielded a non-array that the catch
        // below swallowed — the menu just never changed. One accessor, one shape.
        const cats = await documentsApi.categories();
        if (cancelled) return;
        setDocumentCategoryNav(cats.map(c => ({ id: c.id, name: c.name })));
        setCapTick(t => t + 1);
      } catch {
        // keep the hardcoded Sample Backlog list on 401 / network error
      }
    })();
    return () => { cancelled = true; };
    // navRefreshTick: re-fetch the backlog catalog when an Assign-Dashboard save
    // fires 'ai-nav-config-changed', so a newly-assigned dashboard appears live.
  }, [user, navRefreshTick]);

  /** navTree with its top-level GROUP entries reordered by the admin-set module sort.
   *  Groups with a stored AISortOrder use that value as their sort key.  Groups without
   *  one keep their natural navTree order by interpolating a fractional key between their
   *  neighbouring stored-sort groups, so un-sorted modules stay in their navTree-relative
   *  positions instead of being pushed to the end.  Group SLOTS stay put
   *  (sections/separators don't move) — only which group fills each slot changes.
   *  Falls back to the original tree when no module sort is set. */
  const orderedTree = useMemo(() => {
    if (Object.keys(moduleSort).length === 0) return navTree;
    const groups = navTree.filter(e => e.kind === 'group');
    const entries = groups.map((e, i) => ({
      e,
      i,
      stored: e.kind === 'group' ? (moduleSort[e.group.id] ?? null) : null,
    }));
    // For groups without a stored sort, interpolate a fractional key between the
    // nearest stored-sort neighbours so they stay in their navTree-relative order.
    const withKeys = entries.map((item, arrIdx) => {
      if (item.stored != null) return { e: item.e, key: item.stored };
      let prevKey: number | null = null, prevIdx = -1;
      for (let j = arrIdx - 1; j >= 0; j--) {
        const s = entries[j]?.stored; if (s != null) { prevKey = s; prevIdx = j; break; }
      }
      let nextKey: number | null = null, nextIdx = entries.length;
      for (let j = arrIdx + 1; j < entries.length; j++) {
        const s = entries[j]?.stored; if (s != null) { nextKey = s; nextIdx = j; break; }
      }
      let key: number;
      if (prevKey != null && nextKey != null) {
        key = prevKey + ((arrIdx - prevIdx) / (nextIdx - prevIdx)) * (nextKey - prevKey);
      } else if (prevKey != null) {
        key = prevKey + (arrIdx - prevIdx);
      } else {
        key = (nextKey ?? entries.length) - (nextIdx - arrIdx);
      }
      return { e: item.e, key };
    });
    const sorted = withKeys.sort((a, b) => a.key - b.key).map(x => x.e);
    let gi = 0;
    return navTree.map(e => (e.kind === 'group' ? sorted[gi++]! : e));
  }, [moduleSort]);

  /** Re-order a group's flat items by the admin-set form sort (items with a sort first,
   *  ascending; unsorted keep their original order).  No-op when no form sort is set. */
  function sortItems(items: NavItem[]): NavItem[] {
    if (Object.keys(formSort).length === 0) return items;
    return items
      .map((it, i) => ({ it, key: formSort[it.id] != null ? formSort[it.id]! : 100000 + i }))
      .sort((a, b) => a.key - b.key)
      .map(x => x.it);
  }

  /** Re-order the ITEM children of a group/sub-group by form sort, keeping sub-heads and
   *  sub-groups in their slots (only which item fills each item slot changes). */
  function sortChildren(children: NavGroupChild[]): NavGroupChild[] {
    if (Object.keys(formSort).length === 0) return children;
    const sorted = children
      .filter(c => c.kind === 'item')
      .map((c, i) => ({ c, key: c.kind === 'item' && formSort[c.item.id] != null ? formSort[c.item.id]! : 100000 + i }))
      .sort((a, b) => a.key - b.key)
      .map(x => x.c);
    let ii = 0;
    return children.map(c => (c.kind === 'item' ? sorted[ii++]! : c));
  }

  /** XAF NavigationView ids the current user can see. Empty Set when the
   *  user hasn't been hydrated yet (we fall open in that case — pages still
   *  show, which is correct for a freshly-mounted shell awaiting login data). */
  const allowedViews = useMemo(
    () => new Set((permissions?.allowedViews ?? []).map(v => v.toLowerCase())),
    [permissions],
  );
  /** Dashboard Oids the current user can see (Sample Backlog / ICM). */
  const allowedDashboards = useMemo(
    () => new Set((permissions?.allowedDashboards ?? []).map(s => s.toLowerCase())),
    [permissions],
  );
  /** Normalized NavigationCaption strings — global label-based fallback. */
  const allowedCaptions = useMemo(
    () => new Set((permissions?.allowedCaptions ?? []).map(normCaption)),
    [permissions],
  );
  const isAdmin = permissions?.isAdmin ?? false;

  /** Globally-hidden form ids — the admin "Form Setup" gate.  A form mapped to
   *  `false` is hidden for EVERYONE (ANDed with per-user permission), except
   *  PROTECTED_FROM_HIDE ids which canSeeNavItem keeps on regardless. */
  const hiddenForms = useMemo(
    () => {
      const s = new Set(
        Object.entries(permissions?.formVisibility ?? {})
          .filter(([, v]) => v === false)
          .map(([k]) => k),
      );
      // Report Approval is a workflow stage that only exists when a 2nd signature
      // is required — hide it from the sidebar when approval is off (Default
      // Settings → Report Signing → Require Approval = Off).
      if (!reportApprovalRequired) s.add('repappr');
      // Data Review items 6-9 (Default Settings → SDMS Result Validation / SDMS
      // Result Approval / Result Validation / Result Approval) — ALPACA parity:
      // these are BOTH the getstatus() workflow gate AND the nav-visibility flag
      // (dbo.DefaultSetting.Select) for the SAME row in legacy. Read from
      // `permissions.dataReviewNav` — inlined into the login/permissions
      // payload server-side (AuthController.BuildPermissionsPayloadAsync), NOT
      // a separate post-mount app-config fetch. Two earlier attempts at a
      // fetch-then-render approach (fail open, then fail closed) both showed a
      // visible glitch on first load — items flashing then narrowing, or the
      // whole group appearing late (user report 2026-07-17, both directions).
      // Inlining removes the loading window entirely: `permissions` is either
      // present with the real answer, or not yet loaded (in which case
      // `allowedViews.size === 0` etc. already fails the WHOLE sidebar open a
      // few lines below — consistent with how every other nav item behaves
      // before permissions arrive).
      const dr = permissions?.dataReviewNav;
      if (dr && dr.sdmsResultValidation !== true) s.add('sdmsval');
      if (dr && dr.sdmsResultApproval !== true)   s.add('sdmsappr');
      if (dr && dr.resultValidation !== true)     s.add('rval');
      if (dr && dr.resultApproval !== true)       s.add('rappr');
      // Field Data Validation / Approval — Settings → Default Settings
      // (Sampling Management, FieldDataReview1 / FieldDataReview2). Off = hide
      // the nav item, matching GoLive RecreateNavigationItems() exactly. Read
      // from `permissions.dataReviewNav` (inlined into the login payload) for the
      // SAME reason as the four items above — the previous post-mount app-config
      // fetch with a fail-open useState(true) default made the Field Data Approval
      // item flash visible on login and hide only once that fetch resolved (user
      // report 2026-07-23).
      if (dr && dr.fieldDataValidation !== true) s.add('fielddata-validation');
      if (dr && dr.fieldDataApproval   !== true) s.add('fielddata-approval');
      return s;
    },
    [permissions, reportApprovalRequired],
  );

  /** True when this nav item should appear in the sidebar.  Takes the label
   *  so the caption fallback can do label-vs-NavigationCaption matching when
   *  no explicit NavigationView mapping covers the user's permitted views.
   *  Username threaded through so SERVICE_ONLY items (User Activity) can be
   *  gated to the operator account.  hiddenForms applies the global Form Setup
   *  gate (ANDed with the per-user permission). */
  const canSee = (id: string, label = '') => {
    // AVA Insight mirrors the ALPACA App Config "AVA Enabled" toggle — the
    // same switch that shows/hides the AVA button in the ALPACA header.
    // Applies to everyone (admins included), exactly like ALPACA. Missing
    // value (older payload) fails open.
    if (id === 'avainsight' && permissions?.avaEnabled === false) return false;
    // `permissions != null` = the login/permissions payload has been applied.
    // Before that (boot) canSeeNavItem falls open to avoid a flickering-empty
    // sidebar; once loaded, a non-admin role with nothing linked falls CLOSED
    // (an unlinked role sees no forms — ALPACA parity). (BUG-2 fix 2026-09-03.)
    // Pass the LOGIN username, not the display name: `user.name` is
    // Employee.DisplayName (ALPACA parity) and only equalled "Service" by
    // coincidence on GoLive.  On a database whose Employee row reads
    // "Service User" that comparison failed and every service-only form
    // (Form Config, Default Settings, App Config, Client Version) vanished.
    // Fall back to `name` so a session persisted before `userName` existed
    // keeps its previous behaviour instead of losing access mid-session.
    return canSeeNavItem(id, label, allowedViews, allowedDashboards, allowedCaptions, isAdmin, loginUserName, hiddenForms, permissions != null);
  };

  /** True if the given group child contains at least one item the user
   *  can see (recurses into sub-groups). Used to hide empty
   *  groups/sub-groups so they don't show as ugly empty rows. */
  function hasVisibleLeaves(c: NavGroupChild): boolean {
    if (c.kind === 'subhead') return false;
    if (c.kind === 'item')    return canSee(c.item.id, c.item.label);
    return c.subgroup.children.some(hasVisibleLeaves);
  }
  /** True if a whole top-level group has anything visible (either its flat
   *  `items` list or its structured `children` tree). */
  function groupHasVisibleLeaves(g: { items: { id: string; label: string }[]; children?: NavGroupChild[] }): boolean {
    if (g.items.some(it => canSee(it.id, it.label))) return true;
    if (g.children && g.children.some(hasVisibleLeaves)) return true;
    return false;
  }

  /** Rail icons are MODULE shortcuts — show one when ANY form of its module
   *  is visible to this user.  The previous single-anchor check (canSee on
   *  the rail id alone) wrongly hid e.g. the Sample Backlog icon for a user
   *  granted Backlog 01 but not Missing COC (the icon's hard-wired target),
   *  and the Sampling Management icon when only its anchor page was switched
   *  off in Form Setup (user report 2026-07-24). */
  function railVisible(id: string, title: string): boolean {
    for (const e of navTree) {
      if (e.kind !== 'group') continue;
      if (collectGroupLeafIds(e.group).includes(id)) return groupHasVisibleLeaves(e.group);
    }
    return canSee(id, title);   // top-level items (Home, Executive Dashboard, AVA)
  }

  // Form is reflected in the URL (/m/<key>) so each browser tab can sit on
  // its own form — refresh-safe, duplicable, back/forward works.
  const [page, setPage] = useState(() => moduleKey ?? 'home');
  // Page-scoped top-bar slots (BO UI Standard §5) — the active page can dock a
  // workflow stepper (setTopbarCenter), page badges (setTopbarRight) or a banner.
  // COEXISTS with the legacy #ai-topbar-stepper portal (SDMS / QC Batch keep that);
  // MDL Studies uses these context slots. Cleared whenever the routed page changes.
  const [banner, setBanner] = useState<ReactNode | null>(null);
  const [topbarRight, setTopbarRight] = useState<ReactNode | null>(null);
  const [topbarCenter, setTopbarCenter] = useState<ReactNode | null>(null);
  const bannerApi = useMemo(() => ({ setBanner, setTopbarRight, setTopbarCenter }), []);
  useEffect(() => { setBanner(null); setTopbarRight(null); setTopbarCenter(null); }, [page]);
  // Browser tab title = the open form, G5 style ("ClientCollectors - ALPACA
  // LIMS"; user request 2026-10-13). The label is the sidebar caption, so the
  // tab can never drift from the nav. The "G6" suffix keeps a G6 tab
  // distinguishable from a G5 one open side by side during coexistence.
  useEffect(() => {
    document.title = `${navLabel(page, pageTitles[page] ?? 'Home')} - ALPACA LIMS-G6`;
  }, [page]);
  // Per-form date-source overrides (App Config, ADR-0008 MIGRATION-TEMPORARY) —
  // formId → 'utc' | 'client'.  Loaded from /api/v1/app-config after login.
  const [formDateModes, setFormDateModes] = useState<Record<string, string>>({});
  // Apply the active form's override BEFORE its date columns render (a module
  // setter, not React state, so it must run here in render — not an effect — or
  // the already-rendered columns would keep the previous mode).  Default 'auto'.
  setActiveFormDateMode((formDateModes[page] as 'utc' | 'client' | undefined) ?? 'auto');
  // Bumped when the user re-clicks the nav item they're already on.  Folded
  // into the page component's `key` so the page REMOUNTS (resetting any
  // detail/drill-down view back to its default list) — e.g. clicking "MOR
  // Adhoc Reporting" while viewing a saved report returns to the saved list
  // (user feedback 2026-05-28).
  const [reloadKey, setReloadKey] = useState(0);
  // Custom Reporting drill-down: when the user clicks a JobID on the list,
  // we don't change `page` (keeps the sidebar highlight on Custom Reporting);
  // we just remember which Job is open + flip into detail mode.  Clearing
  // this state returns to the list.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({ 'grp-backlog': false });
  const [query, setQuery] = useState('');
  const [drawer, setDrawer] = useState(false);
  // Desktop sidebar collapse state — user feedback 5/30/2026 #12: add a
  // hamburger to hide the left nav for more content space.  Persisted to
  // localStorage so the user's choice survives reloads.
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('sidebarCollapsed') === 'true'; } catch { return false; }
  });
  // Pre-collapse state while a page has forced the nav collapsed (see ai:nav-collapse below).
  const preCollapseRef = useRef<boolean | null>(null);
  useEffect(() => {
    // Persist the USER's choice only — never a page-forced collapse. While a page holds the nav
    // collapsed, store what it was before. Otherwise signing out from such a page (AppShell unmounts
    // before the page's ai:nav-restore can reach it) left 'true' saved and the next login opened
    // with the navigation hidden (user report 2026-10-02, SDMS Template Builder).
    const persisted = preCollapseRef.current ?? sidebarCollapsed;
    try { localStorage.setItem('sidebarCollapsed', String(persisted)); } catch { /* ignore */ }
    // Broadcast so a page owning a visible toggle (PLM's FullScreen) can show the
    // right pressed state no matter who flipped it — button, Ctrl+\, or a
    // drill-down form's auto-collapse.
    window.dispatchEvent(new CustomEvent('ai:nav-state', { detail: sidebarCollapsed }));
  }, [sidebarCollapsed]);
  // Allow any page to collapse the nav (e.g. Result Entry Express collapses it
  // when entering the detail view, to give the entry grid more horizontal room;
  // QC/Prep Batch detail collapse it when opened from a queue). `ai:nav-restore`
  // returns it to the state it had before the collapse (fired on unmount), so a
  // drill-down form doesn't leave the nav collapsed after the user backs out.
  useEffect(() => {
    const collapse = () => setSidebarCollapsed(prev => { if (preCollapseRef.current === null) preCollapseRef.current = prev; return true; });
    const restore  = () => setSidebarCollapsed(() => { const prev = preCollapseRef.current; preCollapseRef.current = null; return prev ?? false; });
    // `ai:nav-toggle` is the same action as Ctrl+\, exposed to pages that put a
    // visible control on it — PLM's "FullScreen" button, which is exactly what
    // legacy's FullScreen does (plmuirefresh() → OnClick('LPcell',…) collapses
    // the left nav panel; Default.aspx :829). It leaves preCollapseRef alone, so
    // an `ai:nav-restore` on unmount still returns the nav to its pre-form state.
    const toggle   = () => setSidebarCollapsed(prev => !prev);
    // `ai:nav-expand` shows the nav expanded, whatever it was — SDMS leaving Full Screen brings the left
    // navigation back open (user request 2026-09-30). preCollapseRef is left alone, like the toggle.
    const expand   = () => setSidebarCollapsed(false);
    window.addEventListener('ai:nav-collapse', collapse);
    window.addEventListener('ai:nav-restore', restore);
    window.addEventListener('ai:nav-toggle', toggle);
    window.addEventListener('ai:nav-expand', expand);
    return () => {
      window.removeEventListener('ai:nav-collapse', collapse);
      window.removeEventListener('ai:nav-restore', restore);
      window.removeEventListener('ai:nav-toggle', toggle);
      window.removeEventListener('ai:nav-expand', expand);
    };
  }, []);

  // Page-scoped top-bar TITLE override — a drill-down form that swaps in over its
  // list (QC/Prep Batch open from the Analysis/Prep Queue without a route change)
  // can set the top-bar title to its own name via `ai:topbar-title` (detail = the
  // title, or null to clear). Cleared automatically on any real route/page change.
  //
  // The value was UNREAD for a while (the title had been taken out of the top bar
  // in an interim v3 §6 layout) — but BO UI Standard v3 put the title back here,
  // so ignoring it meant every full-page swap kept its HOST page's name: PLM and
  // SDMS both read "QC Batch" in the top bar (user report 2026-09-23). It is read
  // again below; the swapped-in form owns the title while it is on screen.
  //
  // The override is KEYED BY PAGE rather than cleared by an effect: React runs
  // child effects BEFORE the parent's, so a `[page]`-keyed reset would wipe the
  // title a freshly-mounted child had just set on the same commit. Keying it
  // makes a stale override simply not match, with no ordering to get wrong.
  //
  // The detail may also be `{ title, tooltip }`: a form whose title is a record
  // name can put its CONTEXT in the hover text (PLM, prototype v13: "QC Batch
  // QB-260827-04 · Job 260827002 · 6 in sequence"). A plain string keeps
  // working — the tooltip is then the title itself.
  const [titleOverride, setTitleOverride] =
    useState<{ page: string; title: string; tooltip?: string } | null>(null);
  const pageRef = useRef(page);
  pageRef.current = page;
  useEffect(() => {
    const handler = (e: Event) => {
      const d = (e as CustomEvent<string | { title: string; tooltip?: string } | null>).detail ?? null;
      const t = typeof d === 'string' ? { title: d } : d;
      setTitleOverride(t?.title ? { page: pageRef.current, ...t } : null);
    };
    window.addEventListener('ai:topbar-title', handler as EventListener);
    return () => window.removeEventListener('ai:topbar-title', handler as EventListener);
  }, []);

  const term = query.toLowerCase().trim();
  // (v3 §6) The top-bar form title was removed — no `title` local needed here now;
  // <FormTitle> renders the page title once, below the breadcrumb.
  const av = initials(user?.name);

  // Sync state ← URL (direct load, new tab, back/forward). Skip /preview.
  useEffect(() => {
    if (location.pathname === '/preview') return;
    const target = moduleKey ?? 'home';
    setPage((p) => (p === target ? p : target));
  }, [moduleKey, location.pathname]);

  // Keep the nav group that contains the active form EXPANDED, so the user can
  // always see which form is currently open (user feedback 2026-05-28).  Only
  // adds the active group — never collapses ones the user opened manually.
  useEffect(() => {
    const gid = pageGroupId[page];
    if (gid) setOpenGroups((g) => (g[gid] ? g : { ...g, [gid]: true }));
  }, [page]);

  // ── Navigation tracking ───────────────────────────────────────────────────
  // Fire-and-forget audit of which screen the user opened.  Lands in
  // AI_UserActivityLog as EventType='Navigation', Detail='Group · Page'.
  // Deduped via a ref so re-renders on the same page don't spam the log, and
  // gated on an authenticated user.  Errors are swallowed (best-effort audit —
  // a tracking POST must never disrupt the user's navigation).
  const lastTrackedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!user) return;
    if (location.pathname === '/preview') return;     // design-review only
    if (lastTrackedRef.current === page) return;       // already logged this page
    lastTrackedRef.current = page;
    const caption = navLabel(page, page === 'home' ? 'Home' : page);
    const group   = pageGroups[page] ?? '';
    api.post('/api/v1/activity/track', {
      page:   caption,
      group,
      action: 'Navigation',
    }).catch(() => { /* best-effort */ });
  }, [page, user, location.pathname]);

  // Holds the CURRENT open() for the ai:navigate listener below, which is
  // registered once and would otherwise keep calling the first render's copy.
  const openRef = useRef<(id: string) => void>(() => {});

  /**
   * Cross-page navigation, for a page that sends the user to another form —
   * "Open Service Allocation" from a request, "List view" from the schedule.
   *
   * It goes through `open()` rather than a bare `navigate('/m/<id>')` because
   * open() is what actually MOVES the shell: it sets `page` (which chooses the
   * rendered form), closes the drawer, expands the nav group so the sidebar
   * highlights where the user now is, and pushes the URL. A bare navigate()
   * only did the last of those and left the shell showing the previous form.
   *
   * Same idiom as `ai:nav-collapse` above — a window event, so a page nested
   * several components deep does not need a prop threaded down to it.
   */
  useEffect(() => {
    const handler = (e: Event) => {
      const id = (e as CustomEvent<{ page?: string }>).detail?.page;
      if (id) openRef.current(id);
    };
    window.addEventListener('ai:navigate', handler);
    return () => window.removeEventListener('ai:navigate', handler);
  }, []);

  const open = async (id: string) => {
    // Guard forms with unsaved work — navigating away discards what the user
    // typed, so confirm first ("Unsaved changes will be lost. Do you want to
    // proceed?"). Covers EVERY form: registered ones (QC Batch / Prep Batch /
    // SDMS / …) and any form the automatic edit tracker saw the user change.
    if (!(await confirmDiscardIfUnsaved())) {
      // The move was refused, so a record parked for the target form must not
      // survive to hijack the next visit to it (standing rule 10).
      clearPendingRecord();
      return;
    }
    resetUnsavedTracking();   // the page being left takes its edits with it
    // Re-clicking the active nav item resets that page to its default view.
    if (id === page) setReloadKey((k) => k + 1);
    setPage(id);
    setDrawer(false);
    if (location.pathname !== '/preview') {
      const url = id === 'home' ? '/' : `/m/${id}`;
      if (location.pathname !== url) navigate(url);
    }
  };
  // Registered once (see the ai:navigate listener above), so the handler must
  // not capture the first render's `open`. This keeps it on the current one.
  openRef.current = open;

  // Nav items are real <a href> anchors so middle-click / Ctrl+click /
  // Cmd+click and right-click → "Open in new tab" all work natively. Plain
  // left-click intercepts to do SPA-internal navigation (no full reload).
  const hrefFor = (id: string) => id === 'home' ? '/' : `/m/${id}`;

  // A rail icon is a MODULE shortcut: opening it must land on a form the user
  // can actually SEE — the first VISIBLE leaf of its module — not the icon's
  // hard-wired anchor form.  The anchor can be a form this user isn't granted
  // (e.g. the Sampling Management icon's anchor is Field Data Tracking, but a
  // user granted only Field Data Entry must open Entry) — otherwise the icon
  // opens an unauthorized/hidden form (user report 2026-07-24).
  const railTarget = (id: string): string => {
    for (const e of navTree) {
      if (e.kind !== 'group') continue;
      const leaves = collectGroupLeafIds(e.group);
      if (!leaves.includes(id)) continue;
      // Prefer the anchor itself when the user can see it; else the first
      // visible leaf of the same module; else fall back to the anchor.
      if (canSee(id)) return id;
      const firstVisible = leaves.find(lid => canSee(lid));
      return firstVisible ?? id;
    }
    return id;   // standalone Overview items (home / execdash / avainsight)
  };

  // A rail icon stays highlighted for ANY form in its module — match the
  // current page's nav group to the rail item's group (pageGroupId).  Falls
  // back to an exact-id match for the standalone Overview items (home / exec /
  // ava) which aren't inside a nav group.  Previously the rail highlighted only
  // when the page WAS the module's first form.
  const railActive = (railId: string) => {
    if (page === railId) return true;
    const g = pageGroupId[page];
    return !!g && g === pageGroupId[railId];
  };
  const onNavClick = (e: React.MouseEvent, id: string) => {
    // Let the browser take over for modifier-clicks (new tab / new window).
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    open(id);
  };

  // ── Phase 2 · Global + navigation shortcuts (CLAUDE.md standing rule 14, §18.6,
  //    ADR-0013).  Registered once here in the shell so they work on every page:
  //    a sidebar toggle plus Gmail/GitHub-style `g`-prefix nav chords (NAV_CHORDS)
  //    that jump to a module's list view.  Each chord goes through `railTarget` +
  //    `open`, the SAME permission-safe path as clicking that module's rail icon,
  //    so it lands on the first form the user can actually see.
  useHotkeys([
    // Toggle sidebar — Ctrl+\ is the documented key (Keyboard Shortcuts Manual v3);
    // `[` stays a working alias (hidden from the ? sheet so the action shows once).
    { combo: 'ctrl+\\', label: 'Toggle sidebar', group: 'Global', order: 2, run: () => setSidebarCollapsed((c) => !c) },
    { combo: '[', label: 'Toggle sidebar', group: 'Global', order: 2, hidden: true, run: () => setSidebarCollapsed((c) => !c) },
    // Ctrl+J opens AVA Insight (the ALPACA Virtual Assistant) — only when the tenant
    // has AVA enabled, matching the nav-item gate (permissions.avaEnabled).
    ...(permissions?.avaEnabled !== false
      ? [{ combo: 'ctrl+j', label: 'AVA Insight', group: 'Global', order: 3, run: () => { void open('avainsight'); } } as HotkeyDef]
      : []),
    ...NAV_CHORDS.map((c, i): HotkeyDef => ({
      combo: `g ${c.key}`, label: c.label, group: 'Navigation', order: i + 1,
      run: () => { void open(railTarget(c.targetId)); },
    })),
  ]);

  // ── Ctrl/⌘+K "jump to any form" source (ADR-0013 Phase 3, mouse-free launcher).
  //    Publish every navigable leaf THIS user can open (filtered by canSee, the
  //    SAME gate as the sidebar) to the nav-palette store the command palette
  //    merges in.  `open` is captured through the existing `openRef` (kept current
  //    above) so the publish effect only re-runs when permissions / captions
  //    actually change, not every render.
  useEffect(() => {
    const items = navPaletteTargets
      .map((t) => ({ id: t.id, group: t.group, label: navLabel(t.id) }))
      .filter((t) => canSee(t.id, t.label))
      .map((t) => ({ ...t, run: () => { void openRef.current(t.id); } }));
    setNavPaletteItems(items);
    return () => setNavPaletteItems([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions, allowedViews, allowedDashboards, allowedCaptions, isAdmin, hiddenForms, loginUserName]);

  // ── Mouse-free rule (ADR-0013): when a FORM page opens, focus its first FORM
  //    FIELD (the top main control) so keyboard entry starts immediately — the
  //    non-grid counterpart to <AppDataGrid>'s auto-focus-first-row. Works even on
  //    a form that ALSO has a grid (e.g. Sample Registration): `focusFirstFormControl`
  //    ignores grids / toolbars / the search box, so it lands on the top field and
  //    OVERRIDES a row the embedded grid may have grabbed. A pure LIST page has no
  //    such field → it returns null → the grid keeps its own first-row focus.
  //    Guards: never over an open dialog, and never steal a control the user
  //    deliberately focused (but DO override a row an embedded grid took).
  useEffect(() => {
    const id = window.setTimeout(() => {
      if (document.querySelector('[aria-modal="true"]')) return;   // a dialog owns focus
      const content = document.querySelector('.content') as HTMLElement | null;
      if (!content) return;
      const ae = document.activeElement as HTMLElement | null;
      const grid = content.querySelector('.adg');
      if (ae && ae !== document.body && !(grid && grid.contains(ae))) return; // user's own focus
      focusFirstFormControl(content);
    }, 80);
    return () => window.clearTimeout(id);
  }, [page]);

  // ── Alt+↑ / Alt+↓ step a DATE/TIME field's value up / down (user request
  //    2026-09-21: plain segment arrows aren't reliable across browsers, so Alt+↑/↓
  //    changes the value directly — +/−1 day for date & datetime-local (time kept),
  //    +/−1 minute for time, +/−1 month for month). ONE shared window listener, so
  //    it works on every form AND inside dialogs (the Received Date popup, etc.);
  //    never a per-form listener (standing rule 8/14). The value is written through
  //    the native setter + an 'input' event so a controlled React field's onChange
  //    fires and its state updates. Plain ↑/↓ still do the native segment step.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.key !== 'ArrowUp' && e.key !== 'ArrowDown') || !e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      const el = document.activeElement as HTMLInputElement | null;
      if (!el || el.tagName !== 'INPUT' || el.disabled || el.readOnly) return;
      if (!/^(date|time|month|week|datetime-local)$/.test(el.type)) return;
      const next = stepDateInputValue(el.type, el.value, e.key === 'ArrowUp' ? 1 : -1);
      if (next == null) return;                       // week / unknown → let native handle
      e.preventDefault();
      // Write via the native value setter so a CONTROLLED React input notices the
      // change (React ignores a value it did not set) and fires onChange.
      const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
      setter?.call(el, next);
      el.dispatchEvent(new Event('input', { bubbles: true }));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const toggleGroup = (id: string) => setOpenGroups((g) => ({ ...g, [id]: !g[id] }));

  // Step INTO the form from the sidebar — the OPPOSITE of ← (form → sidebar). Focus
  // the form's first field (same rule as auto-focus-on-open); on a PURE list page
  // (no form field) focus the grid's first row; else the first focusable control
  // (user request 2026-09-21: "»» should work opposite").
  const focusFormContent = (): boolean => {
    const content = document.querySelector('.content') as HTMLElement | null;
    if (!content) return false;
    if (focusFirstFormControl(content)) return true;               // a form field
    const row = content.querySelector<HTMLElement>('.adg tbody tr[tabindex="0"], .adg tbody tr');
    if (row) { row.focus(); row.scrollIntoView?.({ block: 'nearest' }); return true; }  // a list grid row
    const first = content.querySelector<HTMLElement>(
      'a[href],button:not([disabled]),input:not([disabled]):not([type=hidden]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
    );
    if (first) { first.focus(); return true; }
    return false;
  };

  // ── Sidebar keyboard navigation (mouse-free operation, ADR-0013). Once focus is
  //    in the sidebar (Tab, or click a nav item), ↑/↓ move between the VISIBLE nav
  //    entries (links + group/subgroup headers; items in a collapsed group are
  //    skipped), Home/End jump; → expands a collapsed group / steps into an open one,
  //    and on a LEAF item → steps INTO the form content (the opposite of ← from a
  //    form); ← collapses a group. Enter/Space activate natively (a link navigates, a
  //    header toggles). Bare arrows aren't registered hotkeys, so the store never conflicts.
  const onNavKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    const key = e.key;
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End', 'ArrowRight', 'ArrowLeft'].includes(key)) return;
    const nav = e.currentTarget;
    const cur = document.activeElement as HTMLElement | null;
    if (!cur || !nav.contains(cur)) return;                 // only when focus is in the sidebar
    const items = Array.from(
      nav.querySelectorAll<HTMLElement>('a.ni, button.nav-group-hdr, button.nav-subgroup-hdr'),
    ).filter((el) => !el.closest('.nav-group-children:not(.open), .nav-subgroup-children:not(.open)'));
    if (items.length === 0) return;
    const idx = items.indexOf(cur);
    const go = (i: number) => {
      const t = items[(i + items.length) % items.length];
      if (t) { t.focus(); t.scrollIntoView({ block: 'nearest' }); }
    };
    const isHdr = cur.classList.contains('nav-group-hdr') || cur.classList.contains('nav-subgroup-hdr');
    switch (key) {
      case 'ArrowDown': e.preventDefault(); go(idx < 0 ? 0 : idx + 1); break;
      case 'ArrowUp': e.preventDefault(); go(idx < 0 ? items.length - 1 : idx - 1); break;
      case 'Home': e.preventDefault(); go(0); break;
      case 'End': e.preventDefault(); go(items.length - 1); break;
      case 'ArrowRight':
        if (isHdr) { e.preventDefault(); if (!cur.classList.contains('open')) cur.click(); else go(idx + 1); }
        else { e.preventDefault(); focusFormContent(); }   // leaf → step into the form (opposite of ←)
        break;
      case 'ArrowLeft':
        if (isHdr && cur.classList.contains('open')) { e.preventDefault(); cur.click(); }
        break;
    }
  };

  // ── Full arrow navigation across a FORM's controls (mouse-free, ADR-0013, user
  //    request 2026-09-21: "top/down/left/right should change control"). `onContentKeyDown`
  //    (below) moves focus to the geometrically nearest control in the pressed
  //    direction — GUARDED so a control that owns the arrow keeps it (text caret,
  //    number step, date segments, native select, radio group, combo value, open
  //    dropdown, the grid's own ↑/↓). ← walks left through the fields and, from the
  //    LEFTMOST control, escapes to the sidebar via `focusSidebarNav`; → from a
  //    sidebar leaf steps back into the form (`focusFormContent`) — a reversible hop.
  const focusSidebarNav = (): boolean => {
    const nav = document.querySelector('nav.sb-scroll') as HTMLElement | null;
    if (!nav) return false;
    const wasCollapsed = sidebarCollapsed;
    if (wasCollapsed) setSidebarCollapsed(false);          // reveal it first if collapsed
    const items = Array.from(
      nav.querySelectorAll<HTMLElement>('a.ni, button.nav-group-hdr, button.nav-subgroup-hdr'),
    ).filter((el) => !el.closest('.nav-group-children:not(.open), .nav-subgroup-children:not(.open)'));
    const target = items.find((el) => el.classList.contains('active')) ?? items[0];
    if (!target) return false;
    // If we just un-collapsed, let the layout settle a tick before focusing.
    window.setTimeout(() => { target.focus(); target.scrollIntoView({ block: 'nearest' }); }, wasCollapsed ? 60 : 0);
    return true;
  };

  // Focus the geometrically NEAREST focusable control inside `scope` in `dir`
  // (spatial navigation). Prefers a candidate on the same row/column (cross-axis
  // distance weighted ×3) then the closest along the pressed axis. Skips the
  // sidebar, itself, and its own ancestors/descendants; a text field gets the
  // caret at its end. Returns true when it moved.
  const focusInDirection = (from: HTMLElement, dir: 'up' | 'down' | 'left' | 'right', scope: HTMLElement): boolean => {
    const SEL = 'a[href],button:not([disabled]),input:not([disabled]):not([type=hidden]),select:not([disabled]),textarea:not([disabled]),[contenteditable="true"],[role="combobox"]:not([aria-disabled="true"]),[role="listbox"]:not([aria-disabled="true"]),[tabindex]:not([tabindex="-1"])';
    const fr = from.getBoundingClientRect();
    const fx = fr.left + fr.width / 2, fy = fr.top + fr.height / 2;
    let best: HTMLElement | null = null, bestScore = Infinity;
    for (const el of Array.from(scope.querySelectorAll<HTMLElement>(SEL))) {
      if (el === from || from.contains(el) || el.contains(from) || el.closest('.sidebar')) continue;
      const r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) continue;                 // not rendered
      const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
      const dx = cx - fx, dy = cy - fy;
      let along: number, across: number;
      if (dir === 'down') { if (dy <= 1) continue; along = dy; across = Math.abs(dx); }
      else if (dir === 'up') { if (dy >= -1) continue; along = -dy; across = Math.abs(dx); }
      else if (dir === 'right') { if (dx <= 1) continue; along = dx; across = Math.abs(dy); }
      else { if (dx >= -1) continue; along = -dx; across = Math.abs(dy); }
      const score = along + across * 3;                              // strongly prefer the same row / column
      if (score < bestScore) { bestScore = score; best = el; }
    }
    if (!best) return false;
    best.focus({ preventScroll: true });
    best.scrollIntoView?.({ block: 'nearest' });
    if (best instanceof HTMLInputElement && /^(text|search|url|tel|email|password|number)$/.test(best.type)) {
      try { const n = best.value.length; best.setSelectionRange(n, n); } catch { /* some types disallow selection */ }
    }
    return true;
  };

  const onContentKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    const key = e.key;
    if (key !== 'ArrowUp' && key !== 'ArrowDown' && key !== 'ArrowLeft' && key !== 'ArrowRight') return;
    // A child control already handled this arrow — an OPEN combo's list nav, a grid
    // row, a portaled picker. React events bubble through the COMPONENT tree, so a
    // popup portaled to document.body still reaches this handler; without this guard
    // the spatial nav ran ON TOP of the combo's own ↑/↓ and yanked focus to a field
    // below, so the list cursor never moved (user report 2026-09-22: ↓ dead on the
    // Result Entry Express QC Batch LookupCombo). Mirrors the hotkey store's guard.
    if (e.defaultPrevented) return;
    if (e.ctrlKey || e.altKey || e.metaKey || e.shiftKey) return;
    if (document.querySelector('[aria-modal="true"]')) return;      // a dialog owns its keys
    const el = e.target as HTMLElement | null;
    if (!el || el.closest('.sidebar')) return;                      // the sidebar handles its own arrows

    const tag = el.tagName;
    const type = ((el as HTMLInputElement).type || '').toLowerCase();
    const role = el.getAttribute('role');
    // A grid DATA ROW owns ↑/↓ (its own row nav): the shared <AppDataGrid> body row
    // (`.adg tbody tr`) AND the hand-rolled <ModuleListView> CRUD list (`.mlv-nav`
    // row). Scoped to the ROW, NOT the whole grid — a column HEADER's sort / filter
    // control is inside `.adg` but does no row-nav, so ↑/↓ there must fall through to
    // spatial nav (↓ steps into the first row, ↑ leaves to the field above) instead
    // of being swallowed and dead-ending (user report 2026-09-22). Without the
    // `.mlv-nav` arm, spatial nav ALSO fired on ↓ over a List-View row and focus
    // jumped unexpectedly (user report 2026-09-21). ←/→ still fall through below so
    // they escape the grid.
    const inGridRow = !!el.closest('.adg tbody tr, .mlv-nav');
    const isTextArea = tag === 'TEXTAREA' || el.isContentEditable;
    const isSelect = tag === 'SELECT';
    const isRadio = tag === 'INPUT' && type === 'radio';
    const isNumber = tag === 'INPUT' && type === 'number';
    const isDateLike = tag === 'INPUT' && /^(date|time|month|week|datetime-local)$/.test(type);
    const isTextInput = tag === 'INPUT'
      && !/^(checkbox|radio|button|submit|reset|range|color|file|number|date|time|month|week|datetime-local)$/.test(type);
    const isCombo = role === 'combobox' || role === 'listbox';
    const comboOpen = el.getAttribute('aria-expanded') === 'true';

    // Controls that fully OWN the arrows keep them (native editing / value / group /
    // segments) — never hijack: textarea/contenteditable, native <select>, radio
    // group, a date/time field, or an OPEN combo/menu.
    if (isTextArea || isSelect || isRadio || isDateLike || (isCombo && comboOpen)) return;

    // Does THIS control consume the pressed arrow? (Conservative: when unsure, it
    // keeps the key — worst case the user Tabs, never broken editing.)
    let consumed = false;
    if (key === 'ArrowUp' || key === 'ArrowDown') {
      // number steps · a closed combo changes its value · the grid moves its rows
      consumed = isNumber || (isCombo && !comboOpen) || inGridRow;
    } else if (isTextInput || isNumber) {
      // a text/number field keeps ← / → while the caret still has room to move
      try {
        const inp = el as HTMLInputElement;
        const atStart = inp.selectionStart === 0 && inp.selectionEnd === 0;
        const atEnd = inp.selectionStart === inp.value.length && inp.selectionEnd === inp.value.length;
        consumed = key === 'ArrowLeft' ? !atStart : !atEnd;
      } catch { consumed = false; }
    }
    if (consumed) return;

    const dir = key === 'ArrowUp' ? 'up' : key === 'ArrowDown' ? 'down' : key === 'ArrowLeft' ? 'left' : 'right';
    if (focusInDirection(el, dir, e.currentTarget)) { e.preventDefault(); return; }
    // Nothing in that direction: ← at the LEFT edge of the form escapes to the sidebar
    // (the mirror of → from the sidebar into the form).
    if (dir === 'left' && focusSidebarNav()) e.preventDefault();
  };
  // Fire-and-forget Logout audit BEFORE clearing the token.
  //
  // The JWT is attached HERE, synchronously, instead of being left to the axios
  // request interceptor.  Interceptors run in a MICROTASK, so the `logout()`
  // below — which is synchronous — used to clear the token first and the call
  // went out with no Authorization header, was rejected 401, and the `.catch`
  // swallowed it.  That is why production held 533 Login rows and ZERO Logout
  // rows (GoLive, 2026-06-01 → 07-25), and why a licence was never released on
  // sign-out (it had to wait out the 5-minute idle timeout).  Pinned by
  // logoutOrdering.test.ts.  The interceptor leaves this header alone once the
  // store is empty (`if (token)`), so no await — and no delay — is needed.
  const handleLogout = () => {
    const bearer = useAuthStore.getState().token;
    // Forget this browser's tab slots: the server has revoked the row, and a
    // leftover slot would make the NEXT sign-in think another tab is still open.
    const sid = sidFromToken(bearer);
    if (sid) clearTabs(sid);
    api.post('/api/v1/auth/logout', {}, {
      headers: bearer ? { Authorization: `Bearer ${bearer}` } : undefined,
    }).catch(() => { /* best-effort */ });
    logout();
    // Clear the activity clock so a stale timestamp cannot immediately sign the
    // NEXT user out the moment they sign in on this browser.
    resetActivity();
    navigate('/login', { replace: true });
  };
  handleLogoutRef.current = handleLogout;
  // User-initiated Sign Out (the two avatar buttons) is guarded like any other
  // way of leaving a form. The idle-timeout auto sign-out calls handleLogout
  // directly (handleLogoutRef above) and is deliberately NOT guarded — nobody
  // is there to answer the prompt, and a stale session must still end.
  const onSignOutClick = () => {
    void confirmDiscardIfUnsaved().then((ok) => { if (ok) handleLogout(); });
  };

  // Help Center header link (ALPACA parity) — opens the external Help Center app
  // in a new tab with a server-minted SSO token. The tab is opened SYNCHRONOUSLY
  // in the click handler (before the await) so the browser treats it as
  // user-initiated and does not block the popup; its URL is set once the launch
  // endpoint returns. Shown only when permissions.helpCenterEnabled (the URL is
  // configured in dbo.AppConfig.Helpcenterurl — never hardcoded here).
  // PREFETCHED launch URL (user request 2026-10-03: "open must be faster"). The
  // SSO token carries no timestamp (DisplayName|Oid|Theme|AppUrl|HCP), so the URL
  // is stable for the signed-in user: it is fetched once in the background as soon
  // as the header knows the link is enabled, and a click then opens the Help Center
  // INSTANTLY — no about:blank tab waiting on a round-trip. Reset when the user
  // changes. A failed prefetch is forgotten so the click retries it.
  const helpUrlRef = useRef<string | null>(null);
  const helpUrlPending = useRef<Promise<string | null> | null>(null);
  const fetchHelpUrl = () => {
    helpUrlPending.current ??= api.get('/api/v1/help-center/launch', { silentLoading: true } as never)
      .then((res) => {
        const url = ((res.data?.data?.url ?? '') as string) || null;
        helpUrlRef.current = url;
        return url;
      })
      .catch(() => { helpUrlPending.current = null; return null; });
    return helpUrlPending.current;
  };
  useEffect(() => {
    helpUrlRef.current = null;
    helpUrlPending.current = null;
    if (permissions?.helpCenterEnabled) void fetchHelpUrl();
  }, [permissions?.helpCenterEnabled, user?.name]);   // eslint-disable-line react-hooks/exhaustive-deps

  const openHelpCenter = () => {
    // Fast path: URL already prefetched → open it directly (noopener is fine here
    // because we never need the tab handle).
    if (helpUrlRef.current) {
      window.open(helpUrlRef.current, '_blank', 'noopener,noreferrer');
      return;
    }
    // Slow path (prefetch not back yet / failed): open the tab SYNCHRONOUSLY so the
    // browser treats it as user-initiated and does not block it, then point it at
    // the URL once it arrives. No 'noopener' here — with it window.open returns null
    // and the tab could never be navigated (user report 2026-10-01); we sever
    // win.opener ourselves instead.
    const win = window.open('', '_blank');
    void fetchHelpUrl().then((url) => {
      if (url && win) { try { win.opener = null; } catch { /* cross-origin — ignore */ } win.location.href = url; }
      else { win?.close(); warnDlg('The help center link is not accessible.'); }
    });
  };

  // Automatic unsaved-edit tracking + in-page exit interception + browser-close
  // prompt for EVERY form (hooks/useUnsavedGuard.ts). Mounted once.
  useEffect(() => installUnsavedTracking(), []);

  // ── Navigation search: keep the MODULE PATH visible ────────────────────────
  // While a search term is active we render the tree filtered to only the
  // branches that contain a hit, KEEPING the group / sub-group headers so every
  // result shows under its module path (e.g. Settings → Utility → Sample Sites).
  // That's what lets a user tell apart the SAME form name that lives in
  // different modules (user request 2026-09-27). Consumed by renderNavChild and
  // the group render below. `matchesTerm` falls open when there's no term.
  // Thin wrappers over the pure predicates in ./navSearch (unit-tested there).
  const matchesTerm = (label: string) => navMatchesTerm(term, label);
  const childHasSearchHit = (c: NavGroupChild) => navChildHasSearchHit(c, term, canSee);
  const groupHasSearchHit = (g: { label: string; items: NavItem[]; children?: NavGroupChild[] }) =>
    navGroupHasSearchHit(g, term, canSee);

  /** Recursively render a sub-group child. `depth` controls indentation —
   *  depth 1 = direct child of top-level group, depth 2 = nested sub-folder, etc. */
  function renderNavChild(c: NavGroupChild, key: number | string, depth: number, forceAll = false): React.ReactNode {
    if (c.kind === 'subhead') {
      // Sub-heads are visual noise while searching — drop them unless an ancestor
      // group label matched (forceAll = "show this whole matched branch").
      if (term && !forceAll) return null;
      return <div key={`sh${key}`} className="nav-subhead">{c.label}</div>;
    }
    if (c.kind === 'item') {
      const it = c.item;
      if (!canSee(it.id, it.label)) return null;
      // While searching, keep only items that match — unless an ancestor group
      // label matched (forceAll), so the whole matched branch stays visible.
      if (term && !forceAll && !matchesTerm(it.label)) return null;
      return (
        <a key={it.id} href={hrefFor(it.id)}
          className={`ni sub nav-depth-${depth}${page === it.id ? ' active' : ''}`}
          title={navLabel(it.id, it.label)}
          onClick={(ev) => onNavClick(ev, it.id)}>
          <NavIcon icon={resolveNavIcon(it.id, it.icon)} color={it.iconColor} />
          {navLabel(it.id, it.label)}
          {it.badge && <span className={`badge b-${it.badge}`}>{badgeText[it.badge]}</span>}
          {it.count != null && <span className="ni-count" title={`${it.count.toLocaleString()} audit entries`}>{it.count.toLocaleString()}</span>}
        </a>
      );
    }
    // subgroup — collapsible folder, can contain further nested children
    const sg = c.subgroup;
    // Hide sub-groups where every descendant item is permission-gated away
    // (recursive — also hides parent of all-empty sub-sub-groups).
    if (!sg.children.some(hasVisibleLeaves)) return null;
    const subMatched = !!term && matchesTerm(sg.label);
    const descHit    = !!term && sg.children.some(childHasSearchHit);
    // While searching, hide a sub-group with no hit in its own label or subtree.
    if (term && !forceAll && !subMatched && !descHit) return null;
    // A label-matched group's whole subtree is relevant → forceAll its children.
    const childForceAll = forceAll || subMatched;
    // Expand while searching to reveal a matching descendant; a group matched
    // only by its OWN label stays at the user's collapse state (its module path
    // is already visible — click to reveal all its children).
    const sgOpen = term ? (descHit || forceAll || !!openGroups[sg.id]) : !!openGroups[sg.id];
    return (
      <div key={sg.id} className="nav-subgroup">
        <button
          className={`nav-subgroup-hdr nav-depth-${depth}${sgOpen ? ' open' : ''}`}
          onClick={() => toggleGroup(sg.id)}
        >
          {/* +/- expand toggle (prototype .exp) — leftmost; '+' collapsed, '–' open. */}
          <span className="exp" aria-hidden="true" />
          <NavIcon icon={resolveNavIcon(sg.id, sg.icon)} color={sg.iconColor} cls="group-icon" />
          <span>{sg.label}</span>
          {sg.badge && <span className={`badge b-${sg.badge}`} style={{ marginLeft: 'auto', marginRight: 6 }}>{badgeText[sg.badge]}</span>}
        </button>
        <div className={`nav-subgroup-children${sgOpen ? ' open' : ''}`}>
          {sortChildren(sg.children).map((cc, ci) => renderNavChild(cc, `${sg.id}-${ci}`, depth + 1, childForceAll))}
        </div>
      </div>
    );
  }

  return (
    <div className="shell">
      {/* Rail */}
      <div className="rail">
        <div className="rail-logo"><i className="ti ti-brain" /></div>
        {railItems.map((r) => {
          if (r.sep) return <div key={r.id} className="rs" />;
          if (!railVisible(r.id, r.title)) return null;
          const target = railTarget(r.id);
          return (
            <a
              key={r.id}
              href={hrefFor(target)}
              className={`ri${railActive(r.id) ? ' active' : ''}`}
              data-title={r.title}
              onClick={(e) => onNavClick(e, target)}
            >
              {/* Rail = the collapsed module strip: use the rail item's OWN
                  distinct icon (r.icon), NOT resolveNavIcon — its anchor is a LEAF
                  id (taskcal, analysisq, …) whose legacy PNG is the shared grey
                  gear, which made every rail slot show the same gear (user report
                  2026-09-18: "only one icon per slot"). One distinct icon per module. */}
              <NavIcon icon={r.icon} color={r.color} />
            </a>
          );
        })}
        <button className="rav" title={user?.name ?? 'Account'} onClick={onSignOutClick}>{av}</button>
      </div>

      {drawer && <div className="sb-backdrop show" onClick={() => setDrawer(false)} />}

      {/* Sidebar — `collapsed` class drives the desktop hide animation;
          `open` slides the mobile drawer overlay. */}
      <div className={`sidebar${drawer ? ' open' : ''}${sidebarCollapsed ? ' collapsed' : ''}`}>
        <div className="sb-brand">
          <img className="sb-brand-img" src={`${import.meta.env.BASE_URL}alpaca-lims-logo.png`} alt="ALPACA LIMS" />
        </div>

        <div className="sb-hdr">
          <div className="sb-hdr-row">
            <span className="sb-title">Navigation</span>
          </div>
          <div className="sb-search">
            <i className="ti ti-search" />
            <input
              type="text" placeholder="Search anything…" value={query}
              aria-label="Search navigation" onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>

        <nav className="sb-scroll" aria-label="Primary" onKeyDown={onNavKeyDown}>
          {(() => {
            // Pre-pass: render every nav entry to a React node OR null. Then
            // strip orphan separators (leading, trailing, consecutive) so an
            // entirely-hidden group doesn't leave behind two stacked dividers.
            const rendered = orderedTree.map((e, i): { node: React.ReactNode; isSep: boolean; isSection: boolean; key: string } => {
              if (e.kind === 'sep') {
                return { node: term ? null : <div key={`s${i}`} className="sep" />, isSep: true, isSection: false, key: `s${i}` };
              }
              if (e.kind === 'section') {
                return { node: term ? null : <div key={`l${i}`} className="sl">{e.label}</div>, isSep: false, isSection: true, key: `l${i}` };
              }
              if (e.kind === 'item') {
                if (term && !fuzzyMatch(term, e.item.label)) return { node: null, isSep: false, isSection: false, key: `i${i}` };
                if (!canSee(e.item.id, e.item.label))                     return { node: null, isSep: false, isSection: false, key: `i${i}` };
                return {
                  node: (
                    <a key={e.item.id} href={hrefFor(e.item.id)}
                      className={`ni${page === e.item.id ? ' active' : ''}`}
                      title={navLabel(e.item.id, e.item.label)}
                      onClick={(ev) => onNavClick(ev, e.item.id)}>
                      <NavIcon icon={resolveNavIcon(e.item.id, e.item.icon)} />{navLabel(e.item.id, e.item.label)}
                      {e.item.badge && <span className={`badge b-${e.item.badge}`}>{badgeText[e.item.badge]}</span>}
                      {e.item.count != null && <span className="ni-count" title={`${e.item.count.toLocaleString()} audit entries`}>{e.item.count.toLocaleString()}</span>}
                    </a>
                  ), isSep: false, isSection: false, key: e.item.id };
              }
              // group
              const g = e.group;
              // Permission-gate flat items + structured children together.
              if (!groupHasVisibleLeaves(g))                          return { node: null, isSep: false, isSection: false, key: g.id };
              // While searching, drop a whole group with no hit anywhere in it.
              if (term && !groupHasSearchHit(g)) {
                return { node: null, isSep: false, isSection: false, key: g.id };
              }
              const groupMatched = !!term && matchesTerm(g.label);
              const useChildren = Array.isArray(g.children) && g.children.length > 0;
              const visibleItems = g.items.filter(it => canSee(it.id, it.label));
              // Flat-items path (groups without a structured tree): while searching
              // keep only matching items — unless the GROUP label itself matched,
              // in which case the whole group is relevant.
              const items = sortItems(
                term && !groupMatched ? visibleItems.filter(it => matchesTerm(it.label)) : visibleItems,
              );
              // Auto-expand toward a hit while searching; a group matched only by
              // its OWN label stays at the user's collapse state (its header — the
              // module name — is already shown; click to reveal all its children).
              const childHit = term
                ? (useChildren ? g.children!.some(childHasSearchHit) : items.length > 0)
                : false;
              const isOpen = term ? (childHit || !!openGroups[g.id]) : !!openGroups[g.id];
              return {
                node: (
                  <div key={g.id}>
                    {/* Header is ALWAYS rendered — even while searching — so every
                        hit shows under its module path (Settings → Utility → …). */}
                    <button className={`nav-group-hdr${isOpen ? ' open' : ''}`} onClick={() => toggleGroup(g.id)}>
                      {/* +/- expand toggle (prototype .exp) — leftmost; '+' collapsed, '–' open. */}
                      <span className="exp" aria-hidden="true" />
                      <NavIcon icon={resolveNavIcon(g.id, g.icon)} color={g.iconColor} cls="group-icon" />
                      <span>{navLabel(g.id, g.label)}</span>
                      {g.badge && <span className={`badge b-${g.badge}`} style={{ marginLeft: 'auto', marginRight: 6 }}>{badgeText[g.badge]}</span>}
                    </button>
                    <div className={`nav-group-children${isOpen ? ' open' : ''}`}>
                      {useChildren
                        ? sortChildren(g.children!).map((c, ci) => renderNavChild(c, ci, 1, groupMatched))
                        : items.map((it) => (
                            <a key={it.id} href={hrefFor(it.id)}
                              className={`ni sub${page === it.id ? ' active' : ''}`}
                              title={navLabel(it.id, it.label)}
                              onClick={(e) => onNavClick(e, it.id)}>
                              <NavIcon icon={resolveNavIcon(it.id, it.icon)} color={it.iconColor} />
                              {navLabel(it.id, it.label)}
                              {it.badge && <span className={`badge b-${it.badge}`}>{badgeText[it.badge]}</span>}
                              {it.count != null && <span className="ni-count" title={`${it.count.toLocaleString()} audit entries`}>{it.count.toLocaleString()}</span>}
                            </a>
                          ))}
                    </div>
                  </div>
                ), isSep: false, isSection: false, key: g.id };
            });

            // Drop orphan separators: leading, trailing, or right after another
            // separator / section header (which already provides visual break).
            const out: React.ReactNode[] = [];
            let lastWasBreak = true;   // start "broken" so a leading sep gets dropped
            for (const r of rendered) {
              if (r.node === null) continue;
              if (r.isSep) {
                if (lastWasBreak) continue;
                out.push(r.node);
                lastWasBreak = true;
              } else {
                out.push(r.node);
                lastWasBreak = r.isSection;
              }
            }
            // Strip trailing separator if any sneaked in.
            while (out.length > 0) {
              const last = out[out.length - 1] as React.ReactElement;
              if (last?.props?.className === 'sep') out.pop();
              else break;
            }
            return out;
          })()}
        </nav>

        <div className="sb-footer" title="Powered by BTSoft · 2026">
          <div className="sb-footer-by">Powered by</div>
          <img src={`${import.meta.env.BASE_URL}btsoft-logo.jpg`} alt="BTSoft" className="sb-footer-logo-img" />
          <span className="sb-footer-ver">v26.10.5</span>
        </div>
      </div>

      {/* Main */}
      <div className="main">
        <div className="topbar">
          <div className="tb-left">
            {/* Hamburger — on desktop (>860px) collapses the sidebar to
                give more screen for data.  On mobile it still toggles the
                drawer overlay.  User feedback 5/30/2026 #12. */}
            <button
              className="hamb"
              aria-label="Toggle navigation"
              title={sidebarCollapsed ? 'Show navigation' : 'Hide navigation'}
              onClick={() => {
                if (window.innerWidth <= 860) setDrawer(d => !d);
                else                          setSidebarCollapsed(c => !c);
              }}
            ><i className="ti ti-menu-2" /></button>
            {/* BO UI Standard v3 (2026-09-18, user request) — the page TITLE lives
                HERE in the top bar, next to the hamburger; the on-page title row is
                dropped (<FormTitle> now renders its chips in the toolbar).
                The leading icon is a FIXED Home glyph on EVERY form (user request
                2026-09-18: "keep the Home-style icon instead of the navigation
                icon"), with the current form's title next to it. */}
            {/* `title` = the hover text: the form's own tooltip when it sent one,
                else the full title, for when a narrow bar ellipsises it. */}
            <span className="topbar-title"
              title={titleOverride?.page === page
                ? (titleOverride.tooltip ?? titleOverride.title)
                : navLabel(page, 'Home')}>
              {/* Home glyph in the brand accent (orange), matching the reference
                  deployment (user request 2026-09-18: "same colour as the marked
                  Home icon"). The accent follows the live theme via --brand-accent. */}
              <i className="ti ti-home tb-title-ic tb-home-ic" aria-hidden="true" />
              {/* A full-page swap (SDMS / PLM / a batch detail) owns the title
                  while it is up — otherwise it would show its HOST page's name. */}
              {/* Its own element so a long title ends in "…" — text-overflow does
                  nothing on the inline-flex span itself, which just clipped it. */}
              <span className="topbar-title-text">
                {titleOverride?.page === page ? titleOverride.title : navLabel(page, 'Home')}
              </span>
            </span>
            {/* Meta slot, immediately AFTER the title: a full-page form portals
                its load-time and unsaved-changes chips here rather than putting
                them on its own action bar (prototype v8's top banner; owner
                screenshot 2026-09-25). Empty — and invisible — for every page
                that does not use it. */}
            <div id="ai-topbar-meta" className="tb-meta-slot" />
          </div>
          {/* Docked workflow-stepper slot (BO UI Standard §5) — centred in the
              top bar.  SDMS / QC Batch portal their <StepIsland> into
              #ai-topbar-stepper (unchanged); MDL Studies renders {topbarCenter}
              here via the banner context.  They are mutually exclusive per page,
              so both share this one centred slot without conflict. */}
          <div id="ai-topbar-stepper" className="tb-stepper-slot">{topbarCenter}</div>
          <div className="tb-right">
            <div id="ai-topbar-nav" className="tb-nav-slot" />
            {/* Record prev/next slot — a full-page detail docks its arrows here,
                between the workflow island and the shell's own controls, which
                is where the prototype's banner puts them. Empty and invisible
                on every page that does not use it. */}
            {topbarRight}
            {/* Top-right alpaca logo removed (user request 2026-07-09). */}
            <span className="vtag">v26.10.5</span>
            {/* Help Center — ALPACA header parity. The ORIGINAL 📖 book emoji, symbol
                only, no words (user decision 2026-10-03, after trying a line icon);
                the name shows as the hover tooltip. Same 28px height as the theme
                and bell controls; shell.css re-centres the emoji. Visible only when
                the external Help Center URL is configured (dbo.AppConfig.Helpcenterurl). */}
            {permissions?.helpCenterEnabled && (
              <button
                className="tb-btn tb-iconlink tb-help"
                title="Help Center"
                aria-label="Help Center"
                onClick={openHelpCenter}
              ><NavIcon icon="📖" /></button>
            )}
            <ThemeSwitcher />
            {/* In-app alerts: unread badge + drop-down (Notification Center In-App channel). */}
            <AlertBell />
            {/* ALPACA parity: show the logged-in user's display name (the
                Employee DisplayName / E-code, same as the legacy header). */}
            {user?.name && <span className="tb-user" title={user.email}>{user.name}</span>}
            <button className="tav" title="Sign out" onClick={onSignOutClick}>{av}</button>
          </div>
        </div>

        <BannerContext.Provider value={bannerApi}>
        {/* Page-docked banner (BO UI Standard §5) — e.g. MDL below-topbar strip. */}
        {banner && <div className="topbanner-slot">{banner}</div>}
        {/* data-nav-group = the open page's sidebar module, so CSS can scope a
            module-wide look (e.g. Data Center's 22px G5 grid rows, shell.css). */}
        <GridLayoutProvider value={pageGroupId[page] === 'grp-datacenter' ? DATA_CENTER_GRID_LAYOUT : NO_GRID_LAYOUT}>
        <div className="content" data-nav-group={pageGroupId[page]} onKeyDown={onContentKeyDown}>
          {page === 'home' ? (
            <div className="page"><HomePage onOpen={open} canSee={canSee} /></div>
          ) : page === 'execdash' ? (
            <ExecutiveDashboardPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page.startsWith('pa-') ? (
            // Performance Analytics — any `pa-<dashId>` nav item renders that
            // dashboard; the category/group nav lives in the left sidebar.
            <PerformanceAnalyticsPage key={`${page}-${reloadKey}`} dashId={page.slice(3)} onOpen={(id) => open('pa-' + id)} />
          ) : page.startsWith('nc-') ? (
            // Notification Center — any `nc-<subId>` nav item renders that
            // sub-page; the module nav lives in the left sidebar (like PA).
            <NotificationCenterPage key={`${page}-${reloadKey}`} subId={page.slice(3)} onOpen={(id) => open('nc-' + id)} />
          ) : isXmlGridBacklog(page) ? (
            // Sample Backlog entries migrated to the OLD native-grid UI whose COLUMNS,
            // FILTERS and data stored-proc are DRIVEN BY the dashboard XML in
            // dbo.DashboardData (client request 2026-10-05): the API parses that
            // dashboard's grid + runs its SP, this renders the native grid — nothing
            // hardcoded, so it matches legacy ALPACA exactly. Migrated one at a time
            // via XML_GRID_BACKLOG (bl-report, bl-coc, …); every other backlog entry
            // keeps the DevExpress dashboard viewer below.
            <BacklogXmlGridPage key={`${page}-${reloadKey}`} navId={page} />
          ) : backlogDashboardId(page) ? (
            // Sample Backlog / ICM entries — Pending Jobs included — show THEIR dbo.DashboardData
            // dashboard in the DevExpress viewer (ADR-0016) — designed in System → Dashboard,
            // view-only here.
            <DashboardViewerPage key={`${page}-${reloadKey}`} pageId={page} dashboardId={backlogDashboardId(page)!} />
          ) : page === 'samplebacklog' || page.startsWith('bl-') || /^icm\d/.test(page) ? (
            <SampleBacklogPage key={`samplebacklog-${reloadKey}`} initialView={page} />
          ) : page === 'jobprog' ? (
            <JobProgressPage key={`jobprog-${reloadKey}`} />
          ) : page === 'testprog' ? (
            <TestProgressPage key={`testprog-${reloadKey}`} />
          ) : page === 'taskcal' ? (
            // Task Calendar — same calendar widget as Sample Scheduling's
            // Scheduled Task Tracking page, surfaced here under Task Tracker.
            <ScheduledTaskPage key={`taskcal-${reloadKey}`} pageId="taskcal" />
          ) : page === 'avainsight' ? (
            <AvaInsightPage key={`${page}-${reloadKey}`} />
          ) : page === 'trendanalysis' ? (
            <TrendAnalysisPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'mdlstudies' ? (
            <MdlStudiesPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'sampaloc' ? (
            <SamplingAllocationPage key={`${page}-${reloadKey}`} />
          ) : page === 'samplinghistory' ? (
            <SamplingHistoryPage key={`${page}-${reloadKey}`} />
          ) : page === 'siteschedfreq' ? (
            <SiteSchedulingFrequencyPage key={`${page}-${reloadKey}`} />
          ) : page === 'set-parameter-default' ? (
            // Settings ▸ Parameter ▸ Parameter Default — the legacy two-tab dashboard, grouped by test
            // and edited in the grid (no detail view in legacy).
            <ParameterDefaultPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'set-contractlab-test-pricing' ? (
            // Settings ▸ Test Pricing ▸ Contract Lab Test Pricing — G5 batch-edit list
            // (Refresh · Add · Remove · Save · Export to · Search), no detail view.
            <ContractLabTestPricingPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'set-sdms-scientificdata' ? (
            // Settings ▸ SDMS ▸ Scientific Data Table — edited in the grid like legacy
            // (Add puts one row in the grid · Save · Delete), so it has no detail view.
            <ScientificDataTablePage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'set-sdms-template-builder' ? (
            // Settings ▸ SDMS ▸ SDMS Template Builder — the legacy list (tick column,
            // New · Refresh · Export to); New / a row open the ONE shared template editor.
            <SdmsTemplateBuilderListPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'set-sdms-template-formula' ? (
            // Settings ▸ SDMS ▸ Template Formula — legacy list (Edit · Refresh · Add Formulas ·
            // Export to) and full-page detail, over the existing /api/v1/sdms-formula.
            <TemplateFormulaPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'set-sdms-seqsetup' || page === 'dc2-sdmsseq' ? (
            // Settings = full editor; Data Center = the same layout, view only.
            <SdmsSequenceSetupPage key={`${page}-${reloadKey}`} onHome={() => open('home')}
              readOnly={page === 'dc2-sdmsseq'} />
          ) : page === 'set-datapackage-testview' ? (
            // Settings ▸ Data Package ▸ Data Package Test View — a BESPOKE page
            // (legacy TestMethodCheckList_DetailView): pick a Test → see its
            // Department + read-only "Check Lists" grid. Not a flat list, so it
            // can't use the shared ModuleListView.
            <DataPackageTestViewPage key={`${page}-${reloadKey}`} />
          ) : page === 'set-field-tests' ? (
            // Settings ▸ Field Template ▸ Field Tests — a BESPOKE page (legacy
            // TestMethod_ListView_FieldTest): list the field tests, then Edit
            // loads EVERY live test with an IsFieldTest checkbox to mark/unmark.
            // The Edit mode swaps the data set (13 ↔ 165) + adds an editable
            // column, which the shared ModuleListView has no slot for.
            <FieldTestsPage key={`${page}-${reloadKey}`} />
          ) : page === 'dc-plm-contamination' ? (
            // Data Center ▸ PLM Contamination Check (G5 parity 2026-10-15) — a
            // query page (filters + Summary / Check Log / Over-limit / All Layers).
            <PlmContaminationPage key={`${page}-${reloadKey}`} />
          ) : page === 'set-plm-reference-library' ? (
            // Settings ▸ Analysis ▸ Reference Library — a BESPOKE list page: its
            // rows open a per-asbestos-type panel and a Control Limits panel sits
            // beneath the grid, neither of which the shared ModuleListView has a
            // slot for. New/Edit opens the shared <SettingsDetailPage> (rule 12).
            <PlmReferenceLibraryPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'reqreg' ? (
            <RequestRegistrationPage key={`${page}-${reloadKey}`} />
          ) : page === 'svcaloc' ? (
            <ServiceAllocationPage key={`${page}-${reloadKey}`} />
          ) : page === 'svcsched' ? (
            <ServiceSchedulingPage key={`${page}-${reloadKey}`} />
          ) : page === 'svctrack' ? (
            <ServiceTrackingPage key={`${page}-${reloadKey}`} />
          ) : page === 'rttrack' ? (
            <RouteTrackingPage key={`${page}-${reloadKey}`} />
          ) : page === 'rgp-preplog' ? (
            // Reagent Preparation (spec §7 / §16.1 / §16.2). Wave 1 = the three
            // lists; the Chemistry / Micro / Calibration header forms and the
            // Prep Notepad follow, and will hang off this same nav id.
            <ReagentPrepLogPage key={`${page}-${reloadKey}`} />
          ) : page === 'rgp-lltsearch' ? (
            <LltSearchPage key={`${page}-${reloadKey}`} />
          ) : page === 'rgp-lltdepletion' ? (
            <LltDepletionPage key={`${page}-${reloadKey}`} />
          ) : page === 'acct-quotes-open' ? (
            // Accounting > Quotes (port of CRMQuotes_ListView_pendingsubmission /
            // _Tracking). Wave 1 = the two lists; the Open Quote detail view, its
            // five child grids and the Submit/Validate/Review workflow follow and
            // will hang off these same nav ids.
            <OpenQuotesPage key={`${page}-${reloadKey}`} />
          ) : page === 'acct-quotes-tracking' ? (
            <QuotesTrackingPage key={`${page}-${reloadKey}`} />
          ) : page === 'icm-requisition' ? (
            detailId ? <RequisitionDetailPage key={`${page}-${detailId}`} /> : <RequisitionListPage key={`${page}-${reloadKey}`} />
          ) : page === 'icm-requisition-review' ? (
            <RequisitionReviewListPage key={`${page}-${reloadKey}`} />
          ) : page === 'icm-purchase-order' ? (
            detailId ? <PurchaseOrderDetailPage key={`${page}-${detailId}`} /> : <PurchaseOrderListPage key={`${page}-${reloadKey}`} />
          ) : page === 'icm-receive-items' ? (
            <ReceiveItemsPage key={`${page}-${reloadKey}`} />
          ) : page === 'icm-distribution' ? (
            <DistributionListPage key={`${page}-${reloadKey}`} />
          ) : page === 'icm-consumption' ? (
            <ConsumptionListPage key={`${page}-${reloadKey}`} />
          ) : page === 'icm-fractional' ? (
            <FractionalConsumptionPage key={`${page}-${reloadKey}`} />
          ) : page === 'icm-disposal' ? (
            <DisposalListPage key={`${page}-${reloadKey}`} />
          ) : page === 'icm-vendor-coa' ? (
            <VendorCoaListPage key={`${page}-${reloadKey}`} />
          ) : page === 'icm-item-init' ? (
            <ItemInitListPage key={`${page}-${reloadKey}`} />
          ) : page === 'icm-stock-watch' ? (
            <StockWatchPage key={`${page}-${reloadKey}`} />
          ) : page === 'icm-stock-alert' ? (
            <StockAlertPage key={`${page}-${reloadKey}`} />
          ) : page === 'icm-expiration-alert' ? (
            <ExpirationAlertPage key={`${page}-${reloadKey}`} />
          ) : page === 'icm-req-tracking' ? (
            <ItemRequisitionTrackingPage key={`${page}-${reloadKey}`} />
          ) : page === 'icm-process-tracking' ? (
            <ItemProcessTrackingPage key={`${page}-${reloadKey}`} />
          ) : page === 'icm-cancelled-requisition' ? (
            <CancelledRequisitionPage key={`${page}-${reloadKey}`} />
          ) : page === 'icm-transaction-history' ? (
            <ItemTransactionHistoryPage key={`${page}-${reloadKey}`} />
          ) : page === 'icm-lt-coa-tracking' ? (
            <LtCoaTrackingPage key={`${page}-${reloadKey}`} />
          ) : page === 'schedtask' ? (
            <ScheduledTaskPage key={`${page}-${reloadKey}`} pageId="schedtask" />
          ) : page === 'set-prelogin' ? (
            <PreLoginSettingsPage key={`${page}-${reloadKey}`} />
          ) : page === 'sampreg' ? (
            <SampleRegistrationPage key={`${page}-${reloadKey}`} />
          ) : page === 'sampreceipt' ? (
            <SampleReceiptNotificationPage key={`${page}-${reloadKey}`} />
          ) : page === 'sampphcheck' ? (
            <SampleContainerPhCheckPage key={`${page}-${reloadKey}`} />
          ) : page === 'sample-in' ? (
            <SampleInPage key={`${page}-${reloadKey}`} />
          ) : page === 'sample-out' ? (
            <SampleOutPage key={`${page}-${reloadKey}`} />
          ) : page === 'sample-disposal' ? (
            <SampleDisposalPage key={`${page}-${reloadKey}`} />
          ) : page === 'sample-disposal-history' ? (
            <SampleDisposalHistoryPage key={`${page}-${reloadKey}`} />
          ) : page === 'sample-inout-log' ? (
            <SampleInOutLogPage key={`${page}-${reloadKey}`} />
          ) : page === 'sample-location' ? (
            <SampleLocationPage key={`${page}-${reloadKey}`} />
          ) : page === 'fielddata-entry' ? (
            <FieldDataEntryPage key={`${page}-${reloadKey}`} />
          ) : page === 'fielddata-validation' ? (
            <FieldDataValidationPage key={`${page}-${reloadKey}`} />
          ) : page === 'fielddata-approval' ? (
            <FieldDataApprovalPage key={`${page}-${reloadKey}`} />
          ) : page === 'fielddata' ? (
            <FieldDataTrackingPage key={`${page}-${reloadKey}`} />
          ) : page === 'samplecancel' ? (
            <SampleCancellationPage key={`${page}-${reloadKey}`} />
          ) : page === 'fieldintake' ? (
            <FieldIntakePage key={`${page}-${reloadKey}`} />
          ) : page === 'samptransfer' ? (
            <SampleTransferPage key={`${page}-${reloadKey}`} />
          ) : page === 'regcompliance' ? (
            <RegulatoryCompliancePage key={`${page}-${reloadKey}`} />
          ) : page === 'dwrc-cats' ? (
            <DwrcCategoriesPage key={`${page}-${reloadKey}`} />
          ) : page === 'dwrc-subcats' ? (
            <DwrcSubCategoriesPage key={`${page}-${reloadKey}`} />
          ) : page === 'useractivity' ? (
            <UserActivityPage key={`${page}-${reloadKey}`} />
          ) : page === 'exceptionlog' ? (
            <ExceptionLogPage key={`${page}-${reloadKey}`} />
          ) : page === 'defaultsettings' ? (
            <DefaultSettingsPage key={`${page}-${reloadKey}`} />
          ) : page === 'formsetup' ? (
            <FormVisibilityPage key={`${page}-${reloadKey}`} />
          ) : page === 'audittrailcfg' ? (
            <AuditTrailConfigurationPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'dashdesigner' ? (
            <DashboardDesignerPage key={`${page}-${reloadKey}`} />
          ) : page === 'dashpreview' ? (
            <DashboardPreviewPage key={`${page}-${reloadKey}`} />
          ) : page === 'dashboards' ? (
            <DashboardsPage key={`${page}-${reloadKey}`} />
          ) : page === 'emailalerts' ? (
            <EmailAlertsPage key={`${page}-${reloadKey}`} />
          ) : page === 'appconfig' ? (
            <AppConfigPage key={`${page}-${reloadKey}`} />
          ) : page === 'clientversion' ? (
            <ClientVersionControlPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'alpacaappconfig' ? (
            <AlpacaAppConfigPage key={`${page}-${reloadKey}`} />
          ) : page === 'screenautolock' ? (
            <ScreenAutoLockPage key={`${page}-${reloadKey}`} />
          ) : page === 'reportidformat' ? (
            <ReportIdFormatPage key={`${page}-${reloadKey}`} />
          ) : page === 'datefilter' ? (
            <DateFilterPage key={`${page}-${reloadKey}`} />
          ) : page === 'set-flutter-defaultsettings' ? (
            <FlutterDefaultSettingsPage key={`${page}-${reloadKey}`} />
          ) : page === 'set-metrc-incoming' ? (
            // Settings ▸ Metrc ▸ Metrc Incoming — LAYOUT port of the legacy
            // MetrcIncoming_DetailView (From/To/Facility + Export + grid). The live
            // METRC-API Facility list + transfer fetch are NOT wired (AI has no METRC
            // integration) — this shows the local incoming history filtered by date.
            <MetrcIncomingPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'hotkeysconfig' ? (
            <KeyboardShortcutsPage key={`${page}-${reloadKey}`} />
          ) : page === 'act-prepflow' ? (
            <ActConfigPage key={`${page}-${reloadKey}`} />
          ) : page === 'workflow-engine' ? (
            <WorkflowConfigListPage key={`${page}-${reloadKey}`} />
          ) : page === 'training-records' ? (
            <TrainingRecordsPage key={`${page}-${reloadKey}`} />
          ) : page === 'control-charts' ? (
            <ControlChartPage key={`${page}-${reloadKey}`} />
          ) : page === 'morlist' ? (
            <MorReportingPage key={`${page}-${reloadKey}`} />
          ) : page === 'mor-template' ? (
            <MorTemplateSetupPage key={`${page}-${reloadKey}`} />
          ) : page === 'morreport' ? (
            <MorAdhocReportingPage key={`morreport-${reloadKey}`} />
          ) : page === 'suboutreg' ? (
            <SuboutRegistrationPage key={`${page}-${reloadKey}`} />
          ) : page === 'subouttest' ? (
            <SuboutTestOrderPage key={`${page}-${reloadKey}`} />
          ) : page === 'suboutnotif' ? (
            <SuboutListPage
              key={`${page}-${reloadKey}`}
              view="notifications"
              title="Subout Notification Queue"
              subtitle="Orders whose results are ready but the contract lab hasn't been notified yet."
            />
          ) : page === 'preplqueue' ? (
            <AnalysisListPage key={`${page}-${reloadKey}`} kind="prep-queue" group="Sample Preparation"
              title="Sample Prep Queue"
              subtitle="Test methods with samples waiting on preparation (PrepMethodCount > 0, IsPrepMethodComplete = false)." />
          ) : page === 'prepbatches' ? (
            <AnalysisListPage key={`${page}-${reloadKey}`} kind="prep-batches" group="Sample Preparation"
              title="Sample Prep Batches"
              subtitle="Prep batches in the last few months grouped by tier." />
          ) : page === 'analysisq' ? (
            <AnalysisListPage key={`${page}-${reloadKey}`} kind="analysis-queue" group="Analysis"
              title="Analysis Queue"
              subtitle="Test methods with samples ready for analysis (SignOff = true, not yet analyzed, not assigned to a QC batch)." />
          ) : page === 'dailyqc' ? (
            // Analysis ▸ Daily QC (2026-09-28) — logbooks → history + trend → full-page entry.
            <DailyQcPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'resexpress' ? (
            <ResultEntryExpressPage key={`${page}-${reloadKey}`} />
          ) : page === 'reformula' ? (
            // Settings ▸ SDMS ▸ Result Entry Formula (2026-07-28) — bespoke page
            // (Analysis Department Chain pattern): mapped Test+Matrix+Method combos
            // + an Add/Edit Formulas picker. STANDALONE — 'formulalib' (Formula
            // Library, the shared definitions) is its OWN full-CRUD form via the
            // generic ModuleListView/MODULE_CRUD below, not a tab of this page.
            <ResultEntryFormulaPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'sdmsformula' ? (
            // Settings ▸ SDMS ▸ SDMS Formula Setup (2026-09-01) — the SDMS-Template
            // parallel to Result Entry Formula Setup: mapped Templates + the SAME
            // Add/Edit/View Formulas picker, backed by the SDMS-side legacy table
            // dbo.SpreadSheetBuilder_TemplateFormula instead of dbo.ResultEntry_Formula.
            <SdmsFormulaPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'set-roundofftest' ? (
            // Settings ▸ Test & Method ▸ Round Off Test (port of legacy XAF "Round Off Test",
            // 2026-09-30) — an interactive rounding-rule test harness; persists nothing. Reuses
            // the verified limsRoundText / formatSigNotation engine.
            <RoundOffTestPage key={`${page}-${reloadKey}`} />
          ) : page === 'set-report-builder' ? (
            // Settings ▸ General ▸ Report Builder (legacy XAF "Report Builder" → the DevExpress
            // ReportsV2 web report designer; owner request 2026-10-01, image 290). Hosts the SAME
            // DevExpress Web Report Designer as the Custom Report Builder, opened on a new report.
            <ReportBuilderPage key={`${page}-${reloadKey}`} />
          ) : page === 'qcbatches' ? (
            // onOpenNav lets the QC Batches list's "New" button jump to the
            // Analysis Queue (QC batches are built from analysis-queue rows). The
            // QC Batch detail form opens inline within AnalysisListPage. (Merged 2026-07-07.)
            <AnalysisListPage key={`${page}-${reloadKey}`} kind="qc-batches" group="Analysis"
              title="QC Batches"
              subtitle="Analytical batches in the workflow — Pending Result Entry → Approved."
              onOpenNav={open} />
          ) : page === 'sdms' ? (
            // SDMS Result Entry (OG nav 'Spreadsheet' / view 'SDMS'). Opened blank
            // from the nav (Retrieve a batch); the QC Batch form opens the same
            // screen seeded with a batch.
            <SdmsResultEntryPage key={`${page}-${reloadKey}`} />
          ) : page === 'plm' ? (
            <PlmPage key={`${page}-${reloadKey}`} />
          ) : page === 'plm-dailyqc' ? (
            <PlmDailyQcListPage key={`${page}-${reloadKey}`} />
          ) : page === 'restrack' ? (
            <AnalysisListPage key={`${page}-${reloadKey}`} kind="result-tracking" group="Analysis"
              title="Result Tracking"
              subtitle="Sample-parameter rows assigned to a QC batch and analyzed (Last month by default)." />
          ) : page === 'sdmsval' ? (
            <AnalysisListPage key={`${page}-${reloadKey}`} kind="result-validation" group="Data Review"
              title="SDMS Result Validation"
              subtitle="Analytical batches awaiting level-2 review (Status = Pending Validation)." />
          ) : page === 'sdmsappr' ? (
            // SDMS Result Approval — batch-level (ALPACA RawDataLevel3BatchReview).
            // Separate from the legacy job-level Result Approval ('rappr'). (Merged 2026-07-07.)
            <AnalysisListPage key={`${page}-${reloadKey}`} kind="sdms-approval" group="Data Review"
              title="SDMS Result Approval"
              subtitle="Analytical batches awaiting SDMS level-3 approval (Status = Pending SDMS Validation)." />
          ) : page === 'rval' ? (
            // Legacy job-level Result Validation (ALPACA QCBatch_ListView_ResultValidation_Filter) —
            // the manual counterpart of 'rappr' below. The list/detail/Validate/Rollback
            // machinery already exists (kind="result-validation-manual" drives
            // ResultValidationDetail in its default mode="validation"); this route was the
            // only missing piece (user report 2026-07-17).
            <AnalysisListPage key={`${page}-${reloadKey}`} kind="result-validation-manual" group="Data Review"
              title="Result Validation"
              subtitle="Analytical batches awaiting level-2 validation (Status = Pending Validation)." />
          ) : page === 'rappr' ? (
            <AnalysisListPage key={`${page}-${reloadKey}`} kind="result-approval" group="Data Review"
              title="Result Approval"
              subtitle="Analytical batches awaiting level-3 approval (Status = Pending Approval)." />
          ) : page === 'custreport' ? (
            // List-view click → per-job detail was REMOVED (2026-06-13).  The
            // only path is: select JobID(s) → Next → combined view (Report /
            // PDF / Save all live there).  No onCustomJobDrill is passed.
            <ReportingListPage key={`${page}-${reloadKey}`} kind="custom"
              title="Custom Reporting"
              subtitle="Every report in the system — list view (Reporting BO, no filter)." />
          ) : page === 'custreportbuilder' ? (
            <CustomReportBuilderPage key={`${page}-${reloadKey}`} />
          ) : page === 'repval' ? (
            <ReportingListPage key={`${page}-${reloadKey}`} kind="validation"
              title="Report Validation"
              subtitle="Reports waiting for level-2 validation (ReportValidatedDate IS NULL)." />
          ) : page === 'repappr' ? (
            <ReportingListPage key={`${page}-${reloadKey}`} kind="approval"
              title="Report Approval"
              subtitle="Reports waiting for level-3 approval (validated, ReportApprovedDate IS NULL)." />
          ) : page === 'morpapp' ? (
            <MorReportingPage key={`${page}-${reloadKey}`} pageId="morpapp" statusFilter={1} breadcrumb="MOR Pending Approval" />
          ) : page === 'repdel' ? (
            <ReportingListPage key={`${page}-${reloadKey}`} kind="delivery"
              title="Report Delivery"
              subtitle="Approved reports at the delivery stage — Pending Delivery and already Sent (ReportApprovedDate IS NOT NULL)." />
          ) : page === 'morpdel' ? (
            // ALPACA LIMS parity: legacy's DrinkingWaterQualityReports_ListView_
            // PendingDelivery criteria is "[Status] = PendingDelivery Or [Status] =
            // Delivered" — both statuses stay visible here, not just still-pending
            // ones (2026-07-17 fix).
            <MorReportingPage key={`${page}-${reloadKey}`} pageId="morpdel" statusFilter={[2, 3]} breadcrumb="MOR Pending Delivery" />
          ) : page === 'reptrack' ? (
            <ReportingListPage key={`${page}-${reloadKey}`} kind="tracking"
              title="Report Tracking"
              subtitle="Full report log with workflow stage badges (validated → approved → delivered)." />
          ) : page === 'batchreport' ? (
            <BatchReportingPage key={`${page}-${reloadKey}`} />
          ) : page === 'edi-import' ? (
            <ExternalDataImportPage key={page} onHome={() => open('home')} />
          ) : page === 'edi-limits' ? (
            <LimitsPage key={page} onHome={() => open('home')} />
          ) : page === 'eddgen' ? (
            <ReportingListPage key={`${page}-${reloadKey}`} kind="edd"
              title="EDD Report Generator"
              subtitle="Electronic Data Deliverable generator history — EDDReportGenerator BO." />
          ) : page === 'eddresult' ? (
            // Separate Spreadsheet View opened by the EDD Report Query popup's OK —
            // tabbed Univer workbook of the generated sheets. (Merged 2026-07-07.)
            <EddSpreadsheetPage key={`${page}-${reloadKey}`} />
          /* 'msr' (Monthly Summary Repository) no longer has a special case —
             it falls through to the shared ModuleListView below (ModuleRegistry
             key 'msr'), which adds the New/Edit dialog, Query panel, Summary and
             AutoGenerate actions (merged team package 2026-07-02).  The earlier
             read-only ReportingListPage kind="msr" view is superseded. */
          ) : page === 'set-icpmsmasspick' ? (
            // ICP-MS Mass Pick is a BATCH-EDIT grid, not a list/detail form: the
            // legacy ICP_MS_MassPick_ListView is InlineEditMode="Batch" with an
            // EMPTY DetailViewID, so the BO has no detail view to route to.
            <IcpMassPickPage key={`${page}-${reloadKey}`} />
          ) : page === 'messagelibrary' ? (
            // System → Message Library. A bespoke page over the XAF-shared
            // dbo.MessageEditor: the library needs a tab strip, a Checks column and
            // a bulk Assign Module, which the generic ModuleListView has no notion
            // of. Its New/Edit detail is still the shared <SettingsDetailPage>
            // (standing rule 12). ADR-0017.
            <MessageLibraryPage key={`${page}-${reloadKey}`} />
          ) : page === 'mydetails' ? (
            <MyDetailsPage key={`${page}-${reloadKey}`} />
          ) : page === 'sec-users' ? (
            <UsersAdminPage key={`${page}-${reloadKey}`} />
          ) : page === 'sec-roles' ? (
            <RolesAdminPage key={`${page}-${reloadKey}`} />
          ) : page === 'sec-mac' ? (
            <MacAdminPage key={`${page}-${reloadKey}`} tab="levels" />
          ) : page === 'sec-rolemac' ? (
            <MacAdminPage key={`${page}-${reloadKey}`} tab="matrix" />
          ) : page === 'sec-navmgr' ? (
            <NavigationManagerPage key={`${page}-${reloadKey}`} />
          ) : page === 'servermon' ? (
            <ServerMonitorPage key={`${page}-${reloadKey}`} />
          ) : page === 'servermon-history' ? (
            <ServerHistoryPage key={`${page}-${reloadKey}`} />
          ) : page === 'sqlconsole' ? (
            <SqlQueryConsolePage key={`${page}-${reloadKey}`} />
          ) : page === 'database-erd' ? (
            // Toolbox ▸ Database ERD — schema diagram in a sandboxed frame, served
            // by the admin-or-service gated /api/v1/database-erd.
            <DatabaseErdPage key={`${page}-${reloadKey}`} />
          ) : page === 'sdms-template-builder' ? (
            // Toolbox ▸ SDMS Template Builder — port of the Alpaca Studio builder; writes the
            // SpreadSheetBuilder_* templates SDMS Result Entry runs.
            <SdmsTemplateBuilderPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page.startsWith('at-') ? (
            // Audit Trail Log entries — Data Center → Audit Trail Log →
            // {Custom Reporting / MOR Reporting / Sample Registration /
            //  …}.  All route to the same full-page AuditTrailPage which
            // hits /api/v1/audit/trail with the form-name resolved from
            // the page id.  See AuditTrailPage.AT_FORM_BY_ID for the
            // page-id → ALPACA FormName mapping.
            <AuditTrailPage
              key={`${page}-${reloadKey}`}
              pageId={page}
              navTitle={navLabel(page)}
            />
          ) : page === 'acc-paymentterms' ? (
            <PaymentTermsPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'acc-region' ? (
            <RegionPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'maint-versionlog' ? (
            <VersionLogPage key={`${page}-${reloadKey}`} />
          ) : page === 'acc-paymentterms-setup' ? (
            <PaymentTermsSetupPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'adc-chain' ? (
            <AnalysisDepartmentChainPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'sm-city' ? (
            <CityPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'sm-division' ? (
            <CompanyPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'sm-division-department' ? (
            <DivisionDepartmentPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'sm-country' ? (
            <CountryPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'sm-state' ? (
            <StatePage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'sm-department' ? (
            <DepartmentPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'sm-glossary' ? (
            <GlossaryPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'sm-message-editor' ? (
            <MessageEditorPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'sm-position' ? (
            <PositionPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'sm-timezones' ? (
            <TimeZonesPage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'sm-language' ? (
            <CurrentLanguagePage key={`${page}-${reloadKey}`} onHome={() => open('home')} />
          ) : page === 'qualifier' ? (
            // Document Management → Qualifier — bespoke redesign (Qualifier-v7
            // prototype): KPI filter tiles, status-striped grid, S/Q/R automation
            // badges. Reads the same /api/v1/modules/qualifier list; New/Edit reuse
            // the shared <ModuleCrudDialog> (the existing detail form) unchanged.
            <QualifierListPage key={`${page}-${reloadKey}`} />
          ) : (page === 'documents' || page.startsWith('documents:')) ? (
            // Document Management → Documents — the published-document reader
            // (BO UI Standard Class E: read-only, no New/Edit/Delete). Replaces
            // ALPACA's runtime-generated per-category nav subtree with one entry
            // and a category rail inside the page (migration log §14.5).
            <DocumentsPage key={`${page}-${reloadKey}`} onHome={() => open('home')}
              initialCategoryId={documentCategoryOf(page)} />
          ) : (
            // Global fallback — ALPACA NavigationItem → ListView parity: ANY nav
            // item without a custom page above opens its list view through the
            // one shared ModuleListView.  Forms with a ModuleRegistry data source
            // show real rows; forms without one show an empty list view (title +
            // toolbar + a "not configured yet" message) instead of a placeholder.
            <ModuleListView
              /* reloadKey folded in so clicking the nav item ALWAYS lands on the
                 LIST view (standing rule 10) — a re-click while the Detail view
                 is open remounts the module back to its list (user 2026-07-02). */
              key={`${page}-${reloadKey}`}
              moduleKey={page}
              group={pageGroups[page] ?? 'Modules'}
              navTitle={navLabel(page)}
              onHome={() => open('home')}
            />
          )}
        </div>
        </GridLayoutProvider>
        </BannerContext.Provider>
      </div>
      {/* In-app alert pop-up stack + the alert detail (position / timing from Pop-up Settings). */}
      <AlertPopupHost />
    </div>
  );
}
